import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import AgoraRTC from 'agora-rtc-sdk-ng';
import axios from 'axios';
import {
  registerDenoiserExtension,
  initializeNoiseCancellation,
  enableNoiseCancellation,
  disableNoiseCancellation,
  setNoiseCancellationMode,
  setNoiseCancellationLevel,
  verifyDenoiserAttached,
  cleanupNoiseCancellation,
  getNoiseCancellationStatus,
  NOISE_CANCELLATION_LEVELS,
} from '../services/noiseCancellation';
import { closePreviewVideoTrack, closePreviewAudioTrack } from '../hooks/useAgoraStubs';
import { toast } from 'react-toastify';
import { useSiteContext } from './SiteContext';
import { useMeetingAppContext } from '../MeetingAppContextDef';

console.log('[AgoraContext] Module loaded - version with screen share support and AI noise cancellation');

// Agora RTC client configuration
AgoraRTC.setLogLevel(1); // 0: DEBUG, 1: INFO, 2: WARNING, 3: ERROR, 4: NONE
AgoraRTC.enableLogUpload();
// IMPORTANT: Register the AI Denoiser extension BEFORE creating any audio tracks
// This must happen at module load time, not in a component lifecycle
registerDenoiserExtension();

// Turn the Agora / getUserMedia error into something the user can act on.
// The silent fallback at join() used to swallow NotAllowedError / NotFoundError
// and leave the user on the OS default with no explanation. Returns null for
// unknown errors so callers can decide whether to toast a generic message.
function describeDeviceError(err, kind /* 'mic' | 'camera' */) {
  const name = err?.name || err?.code || '';
  const label = kind === 'camera' ? 'camera' : 'microphone';
  if (name === 'NotAllowedError' || name === 'PERMISSION_DENIED') {
    return `${label[0].toUpperCase()}${label.slice(1)} permission was denied. Check your browser's site permissions and reload.`;
  }
  if (name === 'NotFoundError' || name === 'DEVICES_NOT_FOUND') {
    return `Your selected ${label} is no longer available — switched to the system default.`;
  }
  if (name === 'NotReadableError' || name === 'TrackStartError' || name === 'DEVICE_IN_USE') {
    return `Your ${label} is in use by another application. Close it and try again.`;
  }
  if (name === 'OverconstrainedError' || name === 'CONSTRAINT_NOT_SATISFIED') {
    return `Couldn't open your ${label} with the requested settings — switched to the default.`;
  }
  if (name === 'AbortError') {
    return `Access to your ${label} was interrupted. Please try again.`;
  }
  return null;
}

const AgoraContext = createContext(null);

// Constants to mirror VideoSDK Constants
export const AgoraConstants = {
  modes: {
    SEND_AND_RECV: 'SEND_AND_RECV',
    RECV_ONLY: 'RECV_ONLY',
  },
  meetingState: {
    CONNECTING: 'CONNECTING',
    CONNECTED: 'CONNECTED',
    DISCONNECTED: 'DISCONNECTED',
    FAILED: 'FAILED',
    RECONNECTING: 'RECONNECTING',
  },
  participantModes: {
    CONFERENCE: 'CONFERENCE',
    VIEWER: 'VIEWER',
  },
  // Permission types (for media device access)
  permission: {
    AUDIO: 'audio',
    VIDEO: 'video',
  },
  // Recording events (matching VideoSDK structure)
  recordingEvents: {
    RECORDING_STARTING: 'RECORDING_STARTING',
    RECORDING_STARTED: 'RECORDING_STARTED',
    RECORDING_STOPPING: 'RECORDING_STOPPING',
    RECORDING_STOPPED: 'RECORDING_STOPPED',
  },
  // Transcription events
  transcriptionEvents: {
    TRANSCRIPTION_STARTING: 'TRANSCRIPTION_STARTING',
    TRANSCRIPTION_STARTED: 'TRANSCRIPTION_STARTED',
    TRANSCRIPTION_STOPPING: 'TRANSCRIPTION_STOPPING',
    TRANSCRIPTION_STOPPED: 'TRANSCRIPTION_STOPPED',
  },
  // Translation events
  translationEvents: {
    TRANSLATION_STARTING: 'TRANSLATION_STARTING',
    TRANSLATION_STARTED: 'TRANSLATION_STARTED',
    TRANSLATION_STOPPING: 'TRANSLATION_STOPPING',
    TRANSLATION_STOPPED: 'TRANSLATION_STOPPED',
  },
  // HLS events
  hlsEvents: {
    HLS_STARTING: 'HLS_STARTING',
    HLS_STARTED: 'HLS_STARTED',
    HLS_PLAYABLE: 'HLS_PLAYABLE',
    HLS_STOPPING: 'HLS_STOPPING',
    HLS_STOPPED: 'HLS_STOPPED',
  },
};

// Video encoder profiles for adaptive local encoding (all 16:9 aspect ratio)
// Uses custom encoder configs to enforce consistent 16:9 across all levels
const VIDEO_PROFILES = {
  high: { width: 1280, height: 720, frameRate: 30, bitrateMax: 1500 },  // Active speaker boost (desktop) — capped at 480p
  medium: { width: 960, height: 540, frameRate: 30, bitrateMax: 800 },  // <=4 participants base (desktop)
  low: { width: 960, height: 540, frameRate: 30, bitrateMax: 800 },   // 5+ participants base (desktop)
  // Webinar stage roles broadcast continuously to a presentation audience —
  // they should be the highest-quality stream in the room. Bumped to 1080p
  // @ 2500kbps (vs 720p @ 1000 for normal "high") so attendees see crisp
  // host video at any audience size. Used by getWebinarStageProfile().
  webinarStage: { width: 1920, height: 1080, frameRate: 30, bitrateMax: 2500 },
};

// Mobile profiles — capped to save bandwidth and battery
// NOTE: All levels use identical settings on purpose. This means the adaptive
// encoder effect is a no-op on mobile (deep comparison will always skip).
// The bitrateMax is set high enough (500kbps) to give the Agora SDK headroom
// so its internal bandwidth estimator doesn't become unstable and starve audio.
const MOBILE_VIDEO_PROFILES = {
  high: { width: 1280, height: 720, frameRate: 30, bitrateMax: 1500 },
  medium: { width: 960, height: 540, frameRate: 30, bitrateMax: 800 },
  low: { width: 960, height: 540, frameRate: 30, bitrateMax: 800 },
  // Mobile webinar stage: pinned to 720p (battery + bandwidth tradeoff —
  // most attendees won't perceive 1080p on a phone-sized presenter tile).
  webinarStage: { width: 1280, height: 720, frameRate: 30, bitrateMax: 1500 },
};



// Detect mobile device (matches useIsMobile hook logic)
function isMobileDevice() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(max-width: 767px)').matches;
}

function getVideoProfiles() {
  return isMobileDevice() ? MOBILE_VIDEO_PROFILES : VIDEO_PROFILES;
}

// Create a portrait video track for mobile by capturing camera directly via getUserMedia.
// Agora's createCameraVideoTrack forces landscape encoding, but getUserMedia preserves
// the phone's natural portrait orientation. This produces vertical video (like Zoom on mobile).
// Tracks created via getUserMedia are stored so we can stop them on cleanup.
let _mobileMediaStream = null;
let _switchingCamera = false;
async function createMobilePortraitTrack(encoderProfile, deviceId) {
  // Stop any previous mobile media stream
  if (_mobileMediaStream) {
    _mobileMediaStream.getTracks().forEach(t => t.stop());
    _mobileMediaStream = null;
  }
  // If a specific deviceId is provided, use it; otherwise default to front camera
  const videoConstraint = deviceId
    ? { deviceId: { exact: deviceId }, width: { ideal: 1280 }, height: { ideal: 720 } }
    : { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } };
  const stream = await navigator.mediaDevices.getUserMedia({
    video: videoConstraint,
  });
  _mobileMediaStream = stream;
  const mediaTrack = stream.getVideoTracks()[0];
  const videoTrack = AgoraRTC.createCustomVideoTrack({
    mediaStreamTrack: mediaTrack,
    encoderConfig: encoderProfile,
    // REVERTED 2026-04-28: 'motion' caused dynamic resolution dips to
    // 360p mid-recording (visible quality degradation that users
    // perceived as broken). 'detail' keeps resolution locked at 720p
    // and lets fps drop under bandwidth pressure. Net result on bursty
    // Wi-Fi is the same total bandwidth — different sacrifice. User
    // explicitly preferred 'detail' (sharp but jerky) over 'motion'
    // (smooth but blurry).
    optimizationMode: 'motion',
  });
  // Ensure cleanup stops the underlying getUserMedia track
  const origClose = videoTrack.close?.bind(videoTrack);
  videoTrack.close = () => {
    if (_mobileMediaStream) {
      _mobileMediaStream.getTracks().forEach(t => t.stop());
      _mobileMediaStream = null;
    }
    if (origClose) origClose();
  };
  return videoTrack;
}

function getAdaptiveVideoProfile(participantCount) {
  const profiles = getVideoProfiles();
  if (participantCount <= 4) return profiles.medium;
  return profiles.low;
}

// Get human-readable resolution label from a profile (object or legacy string)
function getVideoProfileLabel(profile) {
  if (profile && typeof profile === 'object' && profile.width && profile.height) {
    return `${profile.width}x${profile.height}`;
  }
  // Legacy string fallback
  const LEGACY_LABELS = {
    '720p_2': '1280x720',
    '480p_2': '854x480',
    '360p_7': '640x360',
    '1080p_2': '1920x1080',
    '1080p': '1920x1080',
  };
  return LEGACY_LABELS[profile] || profile || 'unknown';
}

// Utility to fetch token from backend
async function getAgoraToken(channelName, uid, role = 'publisher') {
  console.log('[getAgoraToken] Requesting token for channel:', channelName, 'uid:', uid);
  try {
    const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/agora/rtc-token`, {
      channelName,
      uid,
      role
    });
    console.log('[getAgoraToken] Token received successfully');
    return response.data.token;
  } catch (error) {
    console.error('[getAgoraToken] Failed to get Agora token:', error);
    return null;
  }
}

// Helper function to convert string ID to numeric UID for Agora
// Moved outside component so it can be used in hooks
function stringToNumericUid(str) {
  if (!str) return 0;
  if (typeof str === 'number') return str;
  // Convert string to a numeric hash (positive integer)
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

// Screen share UID is the main UID + 1 (to distinguish from webcam)
function getScreenShareUid(mainUid) {
  return mainUid + 1;
}

// Check if a UID is a screen share UID (odd number after main UID)
function isScreenShareUid(uid, mainUid) {
  return uid === mainUid + 1;
}

// Get the main UID from a screen share UID
function getMainUidFromScreenShare(screenShareUid) {
  return screenShareUid - 1;
}

export function AgoraMeetingProvider({
  children,
  config,
  token,
  reinitialiseMeetingOnConfigChange = false,
  joinWithoutUserInteraction = false,
}) {
  // console.log('[AgoraMeetingProvider] Rendering with config:', config?.meetingId, config?.participantId); // commented out to reduce console noise

  const {
    meetingId,
    participantId,
    name: initialParticipantName,
    micEnabled = false,
    webcamEnabled = false,
    mode = AgoraConstants.modes.SEND_AND_RECV,
    metaData = {},
    multiStream = true,
  } = config || {};

  // Mutable participant name — can be updated mid-session without touching the DB
  const [participantName, setParticipantName] = useState(initialParticipantName);

  const { customMeetingName, meetingSettings, isSafari } = useSiteContext();

  // The user picks a microphone (and webcam/speaker) on the joining screen.
  // We read those choices here and keep them in refs so the long-running
  // `join()` and `unmuteMic()` closures always see the latest user selection
  // when they call AgoraRTC.createMicrophoneAudioTrack(). Without this, Agora
  // falls back to the OS default device on first publish — which on machines
  // with Krisp / NVIDIA Broadcast / iShowU / etc. is a virtual device that
  // intercepts (and may break) the user's actual hardware microphone.
  const meetingAppCtx = useMeetingAppContext();
  const selectedMicRef = useRef(meetingAppCtx?.selectedMic || null);
  selectedMicRef.current = meetingAppCtx?.selectedMic || null;
  const selectedWebcamRef = useRef(meetingAppCtx?.selectedWebcam || null);
  selectedWebcamRef.current = meetingAppCtx?.selectedWebcam || null;
  // Helper that returns a non-empty deviceId or undefined (Agora rejects empty strings)
  const getSelectedMicDeviceId = useCallback(() => {
    const id = selectedMicRef.current?.id;
    return (typeof id === 'string' && id.length > 0) ? id : undefined;
  }, []);
  const getSelectedWebcamDeviceId = useCallback(() => {
    const id = selectedWebcamRef.current?.id;
    return (typeof id === 'string' && id.length > 0) ? id : undefined;
  }, []);

  // Create Agora client for main audio/video
  const [client] = useState(() => AgoraRTC.createClient({
    mode: 'rtc',
    codec: 'vp8'
  }));

  // Create separate Agora client for screen sharing (standard Agora pattern)
  const [screenClient] = useState(() => AgoraRTC.createClient({
    mode: 'rtc',
    codec: 'vp8'
  }));

  // State
  const [localAudioTrack, setLocalAudioTrack] = useState(null);
  const [localVideoTrack, setLocalVideoTrack] = useState(null);
  const [localScreenTrack, setLocalScreenTrack] = useState(null);
  const [localScreenAudioTrack, setLocalScreenAudioTrack] = useState(null);

  // Refs for unmount cleanup (useEffect cleanup captures stale state)
  const localAudioTrackRef = useRef(null);
  const localVideoTrackRef = useRef(null);
  const localScreenTrackRef = useRef(null);
  const screenCaptureTrackRef = useRef(null); // Raw screen capture MediaStreamTrack for PiP
  const clientRef = useRef(client);
  const isMicOnRef = useRef(false);
  localAudioTrackRef.current = localAudioTrack;
  localVideoTrackRef.current = localVideoTrack;
  localScreenTrackRef.current = localScreenTrack;

  // Detect when browser/OS kills the local audio track (Bluetooth disconnect, device switch,
  // OS permission revoke, tab backgrounding). Clean up so next toggle creates a fresh track.
  useEffect(() => {
    if (!localAudioTrack) return;

    const handleAudioTrackEnded = () => {
      console.warn('[AgoraContext] Local audio track-ended event fired');
      // Verify the underlying MediaStreamTrack is actually ended before killing mic state.
      // Browsers (especially Chrome on screen share start) can fire spurious track-ended
      // events when the audio device is briefly contended. If we kill the track on these
      // false positives, the user sees their mic flip off mid-meeting and can't recover
      // their state cleanly. Mirrors the screen-share track-ended guard below.
      const mediaTrack = localAudioTrack.getMediaStreamTrack?.();
      const trackState = mediaTrack?.readyState;
      if (trackState === 'ended') {
        console.warn('[AgoraContext] Local audio MediaStreamTrack confirmed ended — cleaning up');
        try { localAudioTrack.close(); } catch (e) { /* ignore */ }
        setLocalAudioTrack(null);
        setIsMicOn(false);
      } else {
        console.log(`[AgoraContext] Audio track-ended fired but readyState=${trackState} — ignoring (may recover)`);
        // Re-check after a delay; only clean up if the track is truly dead
        setTimeout(() => {
          const recheckState = localAudioTrack.getMediaStreamTrack?.()?.readyState;
          if (recheckState === 'ended') {
            console.warn('[AgoraContext] Local audio track confirmed ended after recheck — cleaning up');
            try { localAudioTrack.close(); } catch (e) { /* ignore */ }
            setLocalAudioTrack(null);
            setIsMicOn(false);
          } else {
            console.log('[AgoraContext] Local audio track recovered after track-ended event');
          }
        }, 3000);
      }
    };

    localAudioTrack.on('track-ended', handleAudioTrackEnded);

    return () => {
      localAudioTrack.off('track-ended', handleAudioTrackEnded);
    };
  }, [localAudioTrack]);

  // Browser-level device add/remove detection. The existing track-ended handler
  // above only fires when the OS kills an already-open track; if the user's
  // selected-but-not-yet-opened device goes away (e.g. unplugs a headset while
  // muted), nothing notifies them. This listener re-enumerates and warns when
  // the currently-selected mic/cam disappears, so the user knows why they're
  // silent / on the wrong device. Deliberately does NOT auto-switch — avoids
  // fighting the user's manual selection. visibilitychange is a mobile-Safari
  // fallback where devicechange support is unreliable.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices) return;
    let cancelled = false;

    const checkDevices = async () => {
      if (cancelled) return;
      try {
        const [mics, cams] = await Promise.all([
          AgoraRTC.getMicrophones().catch(() => []),
          AgoraRTC.getCameras().catch(() => []),
        ]);
        const selMicId = selectedMicRef.current?.id;
        const selCamId = selectedWebcamRef.current?.id;
        if (selMicId && mics.length > 0 && !mics.some(d => d.deviceId === selMicId)) {
          console.warn('[AgoraContext] Selected microphone no longer present:', selMicId);
          try { toast.info('Your selected microphone was disconnected. Please pick another in settings.'); } catch (_) {}
        }
        if (selCamId && cams.length > 0 && !cams.some(d => d.deviceId === selCamId)) {
          console.warn('[AgoraContext] Selected camera no longer present:', selCamId);
          try { toast.info('Your selected camera was disconnected. Please pick another in settings.'); } catch (_) {}
        }
      } catch (e) {
        console.warn('[AgoraContext] devicechange re-enumeration failed:', e);
      }
    };

    const onDeviceChange = () => { checkDevices(); };
    const onVisibilityChange = () => {
      // Mobile Safari often fires visibilitychange but not devicechange when
      // returning from background with a newly-connected/disconnected device.
      if (document.visibilityState === 'visible') checkDevices();
    };

    try { navigator.mediaDevices.addEventListener('devicechange', onDeviceChange); } catch (_) {}
    try { document.addEventListener('visibilitychange', onVisibilityChange); } catch (_) {}

    return () => {
      cancelled = true;
      try { navigator.mediaDevices.removeEventListener('devicechange', onDeviceChange); } catch (_) {}
      try { document.removeEventListener('visibilitychange', onVisibilityChange); } catch (_) {}
    };
  }, []);

  const [remoteUsers, setRemoteUsers] = useState(new Map());

  // "Show bot in participants" toggle state — single source of truth.
  // Lifted to React state so the participants useMemo recomputes when it changes.
  const [showSttBot, setShowSttBot] = useState(() => {
    return typeof window !== "undefined" && !!window.__rlinkShowSttBot;
  });
  const showSttBotRef = useRef(showSttBot);
  useEffect(() => {
    if (typeof window !== "undefined") window.__rlinkShowSttBot = showSttBot;
    showSttBotRef.current = showSttBot;
  }, [showSttBot]);
  // Listen for toggle events from the AI Notes top-bar widget.
  // Updates state, then retroactively adds/removes the STT bot from remoteUsers
  // since the user-joined event may have already been filtered out before the toggle flipped.
  useEffect(() => {
    const handler = (e) => {
      const show = e?.detail?.show ?? !!window.__rlinkShowSttBot;
      setShowSttBot(show);
      const c = clientRef.current;
      if (!c) return;
      setRemoteUsers(prev => {
        const next = new Map(prev);
        if (show) {
          const sdkUsers = Array.isArray(c.remoteUsers) ? c.remoteUsers : [];
          sdkUsers.forEach(u => {
            if (u.uid >= 999000 && u.uid <= 999999 && !next.has(u.uid)) {
              console.log('[AgoraContext] Adding STT bot UID retroactively:', u.uid);
              next.set(u.uid, u);
            }
          });
        } else {
          for (const uid of Array.from(next.keys())) {
            if (typeof uid === 'number' && uid >= 999000 && uid <= 999999) {
              next.delete(uid);
            }
          }
        }
        return next;
      });
    };
    window.addEventListener('rlink-show-stt-bot-changed', handler);
    return () => window.removeEventListener('rlink-show-stt-bot-changed', handler);
  }, []);

  const [remoteScreenShares, setRemoteScreenShares] = useState(new Map()); // Separate map for screen shares
  const [remoteUserMetadata, setRemoteUserMetadata] = useState(new Map()); // Store names from PubSub
  const [meetingState, setMeetingState] = useState(AgoraConstants.meetingState.DISCONNECTED);
  const [isMicOn, setIsMicOn] = useState(micEnabled);
  isMicOnRef.current = isMicOn;
  const [isWebcamOn, setIsWebcamOn] = useState(webcamEnabled);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingState, setRecordingState] = useState(AgoraConstants.recordingEvents.RECORDING_STOPPED);
  // Mongo _id of the current Recording doc — exposed so the top-bar
  // hold-overlay can poll the worker's lifecycle and clear once ffmpeg has
  // actually started capturing (RECORDING_STARTED fires when the backend
  // queues the job, not when frames are flowing — there's a 6-8s gap on the
  // worker that was eating the first seconds of every recording).
  const [activeRecordingId, setActiveRecordingId] = useState(null);
  const recordingPollRef = useRef(null);
  const recordingStartedAtRef = useRef(null);
  const recordingStoppedAtRef = useRef(0);
  const currentRecordingIdRef = useRef(null);
  const sessionGroupIdRef = useRef(null);
  const recordingResourceIdRef = useRef(null);
  const recordingSidRef = useRef(null);
  const [presenterId, setPresenterId] = useState(null);
  const [activeSpeakerId, setActiveSpeakerId] = useState(null);
  // Raw loudest speaker — updates every Agora volume event (~200ms) with
  // no debounce. Used ONLY for fast UI cues like the green speaker border.
  // Focus swaps still use activeSpeakerId which is debounced.
  const [instantSpeakerId, setInstantSpeakerId] = useState(null);
  const [sustainedSpeakerId, setSustainedSpeakerId] = useState(null);
  const [pinnedParticipants, setPinnedParticipants] = useState(new Map());
  // Network quality per UID: Map<uid, { uplink: 0-6, downlink: 0-6 }>
  // Agora scale: 0=unknown, 1=excellent, 2=good, 3=poor, 4=bad, 5=very bad, 6=disconnected
  const [networkQualities, setNetworkQualities] = useState(new Map());

  // Current video encoder profile label (e.g. "1280x720") for presence broadcasting
  const [currentVideoProfile, setCurrentVideoProfile] = useState(VIDEO_PROFILES.high);

  // Current encoder optimizationMode ('motion' | 'detail') for presence broadcasting.
  // Mobile is always 'motion' (see createMobilePortraitTrack); desktop flips to
  // 'detail' for the active speaker via the effect that calls setOptimizationMode.
  const [currentOptimizationMode, setCurrentOptimizationMode] = useState('motion');

  // Noise cancellation state
  // DISABLED on mobile — the WASM-based AI denoiser is too CPU-intensive for mobile
  // browsers, causing audio cracking/popping. Desktop has plenty of CPU headroom.
  const [noiseCancellationEnabled, setNoiseCancellationEnabled] = useState(() => {
    if (isMobileDevice()) return false; // Never enable on mobile
    // Load from localStorage
    try {
      const saved = localStorage.getItem('rlink_noise_cancellation');
      if (saved) {
        const settings = JSON.parse(saved);
        return settings.enabled ?? true;
      }
    } catch (e) { }
    return true; // Default to enabled on desktop
  });
  const [noiseCancellationMode, setNoiseCancellationModeState] = useState(() => {
    try {
      const saved = localStorage.getItem('rlink_noise_cancellation');
      if (saved) {
        const settings = JSON.parse(saved);
        return settings.mode ?? 'NSNG';
      }
    } catch (e) { }
    return 'NSNG'; // Default to AI noise gate (High)
  });
  const [noiseCancellationLevel, setNoiseCancellationLevelState] = useState(() => {
    try {
      const saved = localStorage.getItem('rlink_noise_cancellation');
      if (saved) {
        const settings = JSON.parse(saved);
        return settings.level ?? 'AGGRESSIVE';
      }
    } catch (e) { }
    return 'AGGRESSIVE';
  });
  const noiseCancellationEnabledRef = useRef(noiseCancellationEnabled);
  const noiseCancellationModeRef = useRef(noiseCancellationMode);
  const noiseCancellationLevelRef = useRef(noiseCancellationLevel);

  // Refs
  const joinedRef = useRef(false);
  const actualUidRef = useRef(null); // The actual Agora UID used (may differ from numericUid if auto-assigned)
  const configRef = useRef(config);
  const callbacksRef = useRef({});
  const micEnabledRef = useRef(micEnabled);
  const webcamEnabledRef = useRef(webcamEnabled);
  const isScreenSharingRef = useRef(false);
  const presenterIdRef = useRef(null);
  const remoteScreenSharesRef = useRef(new Map());
  const remoteUsersRef = useRef(new Map());
  // Selected speaker (audio output) device ID — applied to every remote audio track
  // via Agora's setPlaybackDevice() so that switching speakers actually routes
  // remote playback (Agora's internal <audio> elements bypass DOM setSinkId()).
  const playbackDeviceIdRef = useRef(null);
  const remoteUserMetadataRef = useRef(new Map());
  const fetchParticipantInfoRef = useRef(null); // Ref to store fetchParticipantInfo function
  const recordingParticipantsRef = useRef(new Map()); // Tracks ALL participants seen during recording: name -> {name, role}
  const meetingParticipantsRef = useRef(new Map()); // Tracks ALL participants seen during entire meeting: name -> {name, role}

  const appId = process.env.REACT_APP_AGORA_APP_ID;

  // Update config ref
  useEffect(() => {
    configRef.current = config;
  }, [config]);

  // Keep mic/webcam enabled refs in sync
  useEffect(() => {
    micEnabledRef.current = micEnabled;
    webcamEnabledRef.current = webcamEnabled;
  }, [micEnabled, webcamEnabled]);

  // Keep screen sharing ref in sync
  useEffect(() => {
    isScreenSharingRef.current = isScreenSharing;
  }, [isScreenSharing]);

  // Keep presenter ID ref in sync
  useEffect(() => {
    presenterIdRef.current = presenterId;
  }, [presenterId]);

  // Keep remote screen shares ref in sync
  useEffect(() => {
    remoteScreenSharesRef.current = remoteScreenShares;
  }, [remoteScreenShares]);

  // Keep remote users ref in sync
  useEffect(() => {
    remoteUsersRef.current = remoteUsers;
  }, [remoteUsers]);

  // Keep remote user metadata ref in sync
  useEffect(() => {
    remoteUserMetadataRef.current = remoteUserMetadata;
  }, [remoteUserMetadata]);

  // Keep noise cancellation refs in sync
  useEffect(() => {
    noiseCancellationEnabledRef.current = noiseCancellationEnabled;
    noiseCancellationModeRef.current = noiseCancellationMode;
    noiseCancellationLevelRef.current = noiseCancellationLevel;
  }, [noiseCancellationEnabled, noiseCancellationMode, noiseCancellationLevel]);

  // Persist noise cancellation settings to localStorage
  useEffect(() => {
    localStorage.setItem('rlink_noise_cancellation', JSON.stringify({
      enabled: noiseCancellationEnabled,
      mode: noiseCancellationMode,
      level: noiseCancellationLevel,
    }));
  }, [noiseCancellationEnabled, noiseCancellationMode, noiseCancellationLevel]);

  // Initialize AI noise cancellation extension on mount
  useEffect(() => {
    console.log('[AgoraContext] Initializing AI noise cancellation...');
    initializeNoiseCancellation().then((success) => {
      if (success) {
        console.log('[AgoraContext] AI noise cancellation initialized successfully');
      } else {
        console.warn('[AgoraContext] AI noise cancellation initialization failed, falling back to browser-level');
      }
    });

    // Cleanup on unmount
    return () => {
      cleanupNoiseCancellation();
    };
  }, []);

  // Disconnect camera/mic/screen on provider unmount (leave or navigate away)
  useEffect(() => {
    return () => {
      [localAudioTrackRef, localVideoTrackRef, localScreenTrackRef].forEach(ref => {
        const track = ref.current;
        if (track) {
          try { track.getMediaStreamTrack()?.stop(); } catch (e) { /* ignore */ }
          try { track.stop(); track.close(); } catch (e) { /* ignore */ }
        }
      });
      const c = clientRef.current;
      if (c && c.connectionState !== 'DISCONNECTED') {
        c.leave().catch(() => { });
      }
      console.log('[AgoraMeetingProvider] Unmount: all tracks stopped and channel left');
    };
  }, []);

  // CLEANUP EFFECT: Ensure presenter is cleared when there are no active screen shares
  // This handles edge cases where cleanup events may have been missed or race conditions occurred
  useEffect(() => {
    // If we have a presenterId but no active screen shares (local or remote), clear the presenter
    if (presenterId && !isScreenSharing && remoteScreenShares.size === 0) {
      console.log('[AgoraContext] Cleanup: Clearing stale presenterId - no active screen shares', {
        presenterId,
        isScreenSharing,
        remoteScreenSharesSize: remoteScreenShares.size
      });
      setPresenterId(null);
      callbacksRef.current.onPresenterChanged?.(null);
    }
  }, [presenterId, isScreenSharing, remoteScreenShares]);

  // ADDITIONAL CLEANUP: Clear presenter if they no longer exist in remoteUsers
  // AND have no active screen share. The screen share check is critical for:
  //   1. The recorder bot in webinar mode (joins as audience, may briefly lack main UID
  //      in remoteUsers while still having the screen share UID in remoteScreenShares)
  //   2. Hosts who present screen with camera/mic off (no main UID published, so they
  //      only exist in remoteScreenShares)
  // Without the screen share check, this cleanup races with polling and the recorder
  // bot never renders the screen share, so webinar recordings drop the screen capture.
  useEffect(() => {
    if (presenterId && !isScreenSharing) {
      const numericPresenterId = Number(presenterId);
      // Check if presenter exists in remote users (any key shape)
      const presenterExists = remoteUsers.has(presenterId) ||
        remoteUsers.has(numericPresenterId) ||
        remoteUsers.has(String(presenterId));

      // Check if there's an active remote screen share for this presenter.
      // remoteScreenShares is keyed by screen share UID (= main UID + 1).
      const expectedScreenShareUid = !isNaN(numericPresenterId) ? numericPresenterId + 1 : null;
      const hasRemoteScreenShare = expectedScreenShareUid != null &&
        (remoteScreenShares.has(expectedScreenShareUid) ||
         remoteScreenShares.has(String(expectedScreenShareUid)));

      // Only clear if neither the main user NOR a screen share for them exists.
      if (!presenterExists && !hasRemoteScreenShare) {
        console.log('[AgoraContext] Cleanup: Presenter no longer in remoteUsers and no screen share, clearing', {
          presenterId,
          remoteUsersSize: remoteUsers.size,
        });
        setPresenterId(null);
        callbacksRef.current.onPresenterChanged?.(null);
      }
    }
  }, [presenterId, isScreenSharing, remoteUsers, remoteScreenShares]);

  // Join meeting
  const join = useCallback(async () => {
    console.log('[AgoraMeetingProvider] join() called, joinedRef:', joinedRef.current, 'meetingId:', meetingId, 'mode:', mode);
    if (joinedRef.current || !meetingId) return;

    // RECV_ONLY mode: force disable mic/webcam to prevent getUserMedia prompts
    if (mode === AgoraConstants.modes.RECV_ONLY) {
      micEnabledRef.current = false;
      webcamEnabledRef.current = false;
    }

    try {
      console.log('[AgoraMeetingProvider] Setting state to CONNECTING');
      setMeetingState(AgoraConstants.meetingState.CONNECTING);
      callbacksRef.current.onMeetingStateChanged?.({ state: 'CONNECTING' });

      // Convert participantId to numeric UID for Agora (Agora requires numeric UIDs)
      // For recorder mode, use the fixed recorder UID so it gets filtered from participant lists
      const recorderUrlParams = new URLSearchParams(window.location.search);
      const isRecorderJoin = recorderUrlParams.get('recorder') === 'true';
      const recorderUidParam = recorderUrlParams.get('uid');
      const numericUid = isRecorderJoin && recorderUidParam ? parseInt(recorderUidParam) : stringToNumericUid(participantId);
      console.log('[AgoraMeetingProvider] Converting participantId to numeric UID:', participantId, '->', numericUid, isRecorderJoin ? '(RECORDER)' : '');

      // Pre-emptively clear any kicking rules from a previous force-end.
      // This prevents UID_BANNED when the host ended the meeting and is rejoining.
      if (!isRecorderJoin) {
        try {
          console.log('[AgoraMeetingProvider] Clearing kicking rules for channel:', meetingId);
          await axios.delete(`${process.env.REACT_APP_BASE_URL}/agora/kicking-rules/${meetingId}`);
        } catch (clearErr) {
          console.warn('[AgoraMeetingProvider] Failed to clear kicking rules (non-fatal):', clearErr.message);
        }
      }

      // Get token from backend — for recorder, use the token from URL params
      let currentUid = numericUid;
      const recorderToken = isRecorderJoin ? recorderUrlParams.get('token') : null;
      console.log('[AgoraMeetingProvider] Token source:', isRecorderJoin ? 'URL (recorder)' : 'API', 'recorderToken:', recorderToken ? recorderToken.substring(0, 30) + '...' : 'null', 'currentUid:', currentUid, 'URL search:', window.location.search.substring(0, 80));
      // Webinar attendees (RECV_ONLY) get audience token — all others get publisher token
      const tokenRole = mode === AgoraConstants.modes.RECV_ONLY ? 'audience' : 'publisher';
      let agoraToken = recorderToken || await getAgoraToken(meetingId, currentUid, tokenRole);
      if (!agoraToken && !token) {
        throw new Error('Failed to get Agora token');
      }

      // Ensure the client is fully disconnected before joining.
      const clientState = client.connectionState;
      if (clientState !== 'DISCONNECTED') {
        console.log('[AgoraMeetingProvider] Client not fully disconnected (state:', clientState, '), forcing leave first');
        try { await client.leave(); } catch (_) { /* ignore */ }
        await new Promise(resolve => setTimeout(resolve, 1500));
      }

      // Join the channel with retry logic.
      // Strategy: try with numeric UID first. If UID_BANNED persists after
      // clearing rules, fall back to uid=0 (Agora auto-assigns a fresh UID).
      const MAX_JOIN_RETRIES = 2;
      const JOIN_TIMEOUT_MS = 15000;
      let uid;
      let clearedRulesOnRetry = false;
      for (let attempt = 0; attempt <= MAX_JOIN_RETRIES; attempt++) {
        try {
          // Before each attempt, ensure client is in DISCONNECTED state
          const preJoinState = client.connectionState;
          if (preJoinState !== 'DISCONNECTED') {
            console.warn(`[AgoraMeetingProvider] Client in ${preJoinState} state before attempt ${attempt + 1}, forcing leave`);
            try { await client.leave(); } catch (_) { /* ignore */ }
            await new Promise(resolve => setTimeout(resolve, 1000));
          }

          console.log(`[AgoraMeetingProvider] Joining channel (attempt ${attempt + 1}/${MAX_JOIN_RETRIES + 1}), uid: ${currentUid}, client state: ${client.connectionState}`);
          if (attempt === 0) meetingParticipantsRef.current = new Map(); // Reset for new meeting

          uid = await Promise.race([
            client.join(appId, meetingId, agoraToken || token, currentUid || null),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('JOIN_TIMEOUT')), JOIN_TIMEOUT_MS)
            ),
          ]);

          console.log('[AgoraMeetingProvider] Join succeeded with uid:', uid);
          break; // Success
        } catch (joinErr) {
          const errMsg = joinErr.message || '';
          const isUidBanned = errMsg.includes('UID_BANNED');
          const isRetryable = isUidBanned || errMsg.includes('WS_ABORT')
            || errMsg.includes('OPERATION_ABORTED') || errMsg.includes('JOIN_TIMEOUT');

          if (errMsg.includes('JOIN_TIMEOUT')) {
            try { await client.leave(); } catch (_) { /* ignore */ }
          }

          if (isRetryable && attempt < MAX_JOIN_RETRIES) {
            console.warn(`[AgoraMeetingProvider] Join failed (${errMsg}), attempt ${attempt + 1}/${MAX_JOIN_RETRIES}`);

            if (isUidBanned && !clearedRulesOnRetry) {
              // First UID_BANNED: try clearing kicking rules again and retry with same UID
              console.log('[AgoraMeetingProvider] UID_BANNED — clearing kicking rules and retrying');
              clearedRulesOnRetry = true;
              try {
                await axios.delete(`${process.env.REACT_APP_BASE_URL}/agora/kicking-rules/${meetingId}`);
              } catch (_) { /* ignore */ }
              await new Promise(resolve => setTimeout(resolve, 2000));
              continue;
            } else if (isUidBanned && clearedRulesOnRetry) {
              // Second UID_BANNED: fall back to uid=0 (auto-assign)
              console.log('[AgoraMeetingProvider] UID_BANNED persists — falling back to uid=0 (auto-assign)');
              currentUid = 0;
              agoraToken = await getAgoraToken(meetingId, 0);
              if (!agoraToken) {
                throw new Error('Failed to get fallback token for uid=0');
              }
              await new Promise(resolve => setTimeout(resolve, 1000));
              continue;
            }

            await new Promise(resolve => setTimeout(resolve, 3000));
            continue;
          }

          console.error(`[AgoraMeetingProvider] Join failed permanently after ${attempt + 1} attempts:`, errMsg);
          throw joinErr;
        }
      }

      joinedRef.current = true;
      actualUidRef.current = uid; // Store actual UID (may be auto-assigned if uid=0 was used)
      console.log('[AgoraMeetingProvider] Stored actual UID:', uid, '(requested:', numericUid, ')');

      // Immediately set CONNECTED and fire callbacks so the UI renders.
      // Track creation happens asynchronously below and should not block the UI.
      console.log('[AgoraMeetingProvider] Successfully joined, setting state to CONNECTED');
      setMeetingState(AgoraConstants.meetingState.CONNECTED);
      callbacksRef.current.onMeetingStateChanged?.({ state: 'CONNECTED' });
      callbacksRef.current.onMeetingJoined?.();

      // Check if recording is already in progress (for late joiners / other participants)
      try {
        const statusRes = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/recording/status/${meetingId}`
        );
        if (statusRes.data.isRecording) {
          console.log('[AgoraContext] Recording already in progress — syncing state');
          setRecordingState(AgoraConstants.recordingEvents.RECORDING_STARTED);
          setIsRecording(true);
          setIsTranscribing(!!statusRes.data.isTranscribing);
          callbacksRef.current.onRecordingStateChanged?.({ status: AgoraConstants.recordingEvents.RECORDING_STARTED });
        }
      } catch (_) { /* ignore */ }

      // Prewarm disabled — was causing SID mismatches

      // Enable audio volume indicator for active speaker detection
      try {
        client.enableAudioVolumeIndicator(200); // Fire every 200ms for responsive speaker detection
      } catch (err) {
        console.warn('[AgoraMeetingProvider] Failed to enable audio volume indicator:', err);
      }

      // Recorder-only diagnostic: log Agora SDK stats every 5s so we can
      // see EXACTLY where the per-recorder fps wall is — Agora delivery,
      // Chrome decode, or compositor render. The user reports their local
      // browser receives smooth streams; our recorder gets ~4-7 fps. This
      // logs the recorder's view of:
      //   receiveFrameRate         — what Agora SD-RTN delivers over network
      //   decoderOutputFrameRate   — what Chrome's H.264 decoder outputs
      //   rendererOutputFrameRate  — what hits the <video> element
      // Diagnosing flow:
      //   recv high + render low  → Chrome compositor / CPU starvation
      //   recv low                → Agora is degrading the recorder
      // Worker's BrowserConsole filter passes [Recorder] prefix logs
      // through, so these end up in pm2 logs alongside the lifecycle.
      if (isRecorderJoin) {
        // C1 — expose the Agora client globally on recorder pages so the
        // worker can query getRemoteVideoStats() via puppeteer.evaluate at
        // capture start and stamp the Recording with detected per-uid
        // source resolution. Never set on real publisher pages.
        if (typeof window !== 'undefined') window.__AGORA_CLIENT__ = client;
        const tickStats = () => {
          try {
            const allVideo = client.getRemoteVideoStats?.() || {};
            const rtcStats = client.getRTCStats?.() || {};
            const lines = [];
            for (const [uid, s] of Object.entries(allVideo)) {
              lines.push(
                `uid=${uid}` +
                ` recv=${s.receiveFrameRate ?? '?'}fps` +
                ` decode=${s.decoderOutputFrameRate ?? '?'}fps` +
                ` render=${s.rendererOutputFrameRate ?? '?'}fps` +
                ` w=${s.receiveResolutionWidth ?? '?'}x${s.receiveResolutionHeight ?? '?'}` +
                ` bps=${s.receiveBitrate ?? '?'}` +
                ` lost=${s.packetLossRate ?? '?'}%`,
              );
            }
            console.log(
              `[Recorder] AGORA_STATS rtt=${rtcStats.RTT ?? '?'}ms users=${Object.keys(allVideo).length}` +
              ` | ${lines.join(' || ') || 'no remote video stats yet'}`,
            );
          } catch (e) {
            console.warn('[Recorder] AGORA_STATS error:', e?.message || e);
          }
        };
        // First tick after 5s (let initial subscribe settle), then every 5s.
        // Use setInterval (not recursive setTimeout) so a single id can be
        // cleared in leave() — the old recursive form re-assigned the local
        // var but the window-level handle pointed at the already-fired first
        // timeout, so leave() couldn't actually stop the loop.
        const recorderStatsTimer = setInterval(tickStats, 5000);
        if (typeof window !== 'undefined') window.__rlinkRecorderStatsTimer = recorderStatsTimer;
      }

      // Enable dual-stream simulcasting — Phase 3 v2 (2026-04-27).
      //
      // Yesterday's v1 attempt regressed per-track WebM from 10 fps → 1.5
      // fps. The bug: enableDualStream was called on EVERY client
      // including the recv-only recorder bot. The recorder doesn't publish
      // anything so calling enableDualStream there is meaningless; we
      // think it triggered some subscribe-handshake overhead (video_ready
      // jumped from 2.8s to 13.6s) and possibly raced with the recorder's
      // setRemoteVideoStreamType(uid, 0) call, dropping frames during
      // the early subscribe window.
      //
      // v2 scoped fix: ONLY enable on publisher clients (i.e. real
      // participants). The recorder client takes the recv-only path
      // and does NOT call enableDualStream — it just consumes whichever
      // stream it requests via setRemoteVideoStreamType.
      //
      // Why this matters: the recorder bot calls setRemoteVideoStreamType
      // (user.uid, 0) on every remote user (~line 1950) which requests
      // the HIGH stream. WITHOUT dual-stream enabled on the publisher,
      // that call is a no-op — publisher only sends one stream. WITH
      // dual-stream enabled on the publisher only, the recorder gets
      // the publisher's full source rate independent of other receivers.
      //
      // Total publisher upload: ~1.8 Mbps (HIGH 1.0 + LOW 0.8). Fine for
      // typical home connections (5-20 Mbps upload).
      // Phase 3 v3 (2026-04-27): re-enable dual-stream simulcasting now
      // that the per-track MediaRecorder pipeline (Phase 3 v3a in
      // MeetingContainerNew.js) handles track replacement. When Agora
      // swaps the underlying track on stream-type switch, the old
      // track fires 'ended' and the polling loop spawns a new
      // MediaRecorder for the new track — segment counter advances,
      // chunks land in `rlink-pv-<recId>-<uid>-s<N>.webm`, compose
      // pipeline concats segments before xstack input.
      //
      // The !isRecorderJoin guard remains: only real publishers enable
      // dual-stream. Recorder bot is recv-only, doesn't need to.
      //
      // Total publisher upload: ~1.8 Mbps (HIGH 1.0 + LOW 0.8). Fine
      // for typical home connections. The recorder's existing
      // setRemoteVideoStreamType(uid, 0) call (line ~1950) now actually
      // takes effect — recorder receives the HIGH stream regardless of
      // other receivers' bandwidth.
      if (!isRecorderJoin) {
        try {
          await client.enableDualStream();
          client.setLowStreamParameter({
            width: 320,
            height: 180,
            framerate: 30,
            bitrate: 800,
          });
        } catch (err) {
          console.warn('[AgoraMeetingProvider] Failed to enable dual-stream:', err);
        }
      }



      // Create and publish local tracks if enabled and mode allows
      const shouldEnableMic = micEnabledRef.current;
      const shouldEnableWebcam = webcamEnabledRef.current;
      console.log('[AgoraMeetingProvider] Creating tracks - mic:', shouldEnableMic, 'webcam:', shouldEnableWebcam);

      if (mode !== AgoraConstants.modes.RECV_ONLY) {
        // Close any preview tracks before creating meeting tracks
        // This prevents "can not find stream after getUserMedia" errors
        closePreviewAudioTrack();
        closePreviewVideoTrack();

        if (shouldEnableMic) {
          try {
            // Honor the device the user picked on the joining screen. If they
            // never picked one (or the browser has no permission yet), Agora
            // falls back to the OS default device.
            const userMicId = getSelectedMicDeviceId();
            console.log('[AgoraMeetingProvider] Creating mic track with deviceId:', userMicId || '(default)');
            let audioTrack;
            try {
              audioTrack = await AgoraRTC.createMicrophoneAudioTrack({
                microphoneId: userMicId,
                AEC: true,  // Acoustic Echo Cancellation
                AGC: true,  // Automatic Gain Control - normalizes volume for quiet speakers
                ANS: true,  // Automatic Noise Suppression (browser-level)
              });
            } catch (deviceErr) {
              // Selected device may have been unplugged / become busy between
              // the joining screen and now. Retry without the deviceId so the
              // user still gets audio (just on the OS default).
              if (userMicId) {
                console.warn('[AgoraMeetingProvider] Mic create failed for', userMicId, '— retrying with default device:', deviceErr.message);
                const msg = describeDeviceError(deviceErr, 'mic');
                if (msg) { try { toast.info(msg); } catch (_) { /* toast optional */ } }
                audioTrack = await AgoraRTC.createMicrophoneAudioTrack({
                  AEC: true, AGC: true, ANS: true,
                });
              } else {
                throw deviceErr;
              }
            }
            setLocalAudioTrack(audioTrack);

            // Apply AI noise cancellation if enabled (desktop only)
            if (noiseCancellationEnabledRef.current && !isMobileDevice()) {
              try {
                await enableNoiseCancellation(audioTrack, noiseCancellationModeRef.current, noiseCancellationLevelRef.current);
              } catch (ncErr) {
                console.warn('[AgoraContext] Failed to apply AI noise cancellation:', ncErr);
              }
            }

            await client.publish(audioTrack);
            setIsMicOn(true);
          } catch (err) {
            console.error('Failed to create/publish audio track:', err);
            const msg = describeDeviceError(err, 'mic');
            try { toast.error(msg || 'Could not access your microphone. Please check your device and try again.'); } catch (_) {}
          }
        }

        if (shouldEnableWebcam) {
          try {
            const initialProfile = getVideoProfiles().high;
            const userCamId = getSelectedWebcamDeviceId();
            console.log('[AgoraMeetingProvider] Creating camera track with deviceId:', userCamId || '(default)');
            let videoTrack;
            try {
              if (isMobileDevice()) {
                videoTrack = await createMobilePortraitTrack(initialProfile, userCamId);
              } else {
                videoTrack = await AgoraRTC.createCameraVideoTrack({
                  cameraId: userCamId,
                  encoderConfig: initialProfile,
                  // REVERTED to 'detail' 2026-04-28 per user preference:
                  // 'motion' kept fps high but visibly dropped resolution
                  // to 360p on Wi-Fi bursts, which read as "broken" video.
                  // 'detail' keeps 720p locked and lets fps dip under
                  // bandwidth pressure — same total bandwidth, different
                  // visible failure mode. User chose sharp+jerky over
                  // smooth+blurry.
                  optimizationMode: 'motion',
                });
              }
            } catch (trackErr) {
              console.warn('[AgoraContext] Camera track failed with profile, retrying with defaults:', trackErr.message || trackErr);
              const msg = describeDeviceError(trackErr, 'camera');
              if (msg) { try { toast.info(msg); } catch (_) {} }
              // Retry without device id and without profile, in that order, so a stale
              // selectedWebcam id (e.g. unplugged) doesn't cause a hard failure.
              try {
                videoTrack = await AgoraRTC.createCameraVideoTrack({
                  cameraId: userCamId,
                  optimizationMode: 'motion',
                });
              } catch (_) {
                videoTrack = await AgoraRTC.createCameraVideoTrack({ optimizationMode: 'motion' });
              }
            }
            setLocalVideoTrack(videoTrack);
            await client.publish(videoTrack);
            setIsWebcamOn(true);
          } catch (err) {
            console.error('[AgoraContext] Failed to create/publish video track (all attempts):', err);
            const msg = describeDeviceError(err, 'camera');
            try { toast.error(msg || 'Could not access your camera. Please check your device and try again.'); } catch (_) {}
          }
        }
      }
    } catch (error) {
      console.error('[AgoraMeetingProvider] Failed to join meeting:', error);
      setMeetingState(AgoraConstants.meetingState.FAILED);
      callbacksRef.current.onMeetingStateChanged?.({ state: 'FAILED' });
      callbacksRef.current.onError?.({ code: 'JOIN_FAILED', message: error.message });
    }
  }, [client, appId, meetingId, participantId, mode, token]);

  // Leave meeting
  const leave = useCallback(async () => {
    try {
      // Stop the recorder-stats interval if it was started in join()
      if (typeof window !== 'undefined' && window.__rlinkRecorderStatsTimer) {
        clearInterval(window.__rlinkRecorderStatsTimer);
        window.__rlinkRecorderStatsTimer = null;
      }
      // Stop and close local tracks, also stop underlying MediaStreamTracks
      // to ensure the browser camera/mic indicator light turns off
      if (localAudioTrack) {
        try { localAudioTrack.getMediaStreamTrack()?.stop(); } catch (e) { /* ignore */ }
        localAudioTrack.stop();
        localAudioTrack.close();
        setLocalAudioTrack(null);
      }
      if (localVideoTrack) {
        try { localVideoTrack.getMediaStreamTrack()?.stop(); } catch (e) { /* ignore */ }
        localVideoTrack.stop();
        localVideoTrack.close();
        setLocalVideoTrack(null);
      }
      // Full screen share teardown: stops screenClient, audio track, PiP clone, notifies backend
      if (localScreenTrack && disableScreenShareRef.current) {
        await disableScreenShareRef.current();
      }

      // Clean up local participant info from backend (use actual UID which may be auto-assigned)
      if (meetingId && participantId) {
        const cleanupUid = actualUidRef.current || stringToNumericUid(participantId);
        axios.delete(
          `${process.env.REACT_APP_BASE_URL}/agora/participant-info/${meetingId}/${cleanupUid}`
        ).catch(err => console.error('Failed to cleanup local participant info:', err));
      }

      // Ensure client.leave() fully completes to prevent UID_BANNED on rejoin.
      // Only call leave if the client is actually connected/connecting.
      const clientState = client.connectionState;
      if (clientState !== 'DISCONNECTED') {
        console.log('[AgoraMeetingProvider] Leaving Agora channel (state:', clientState, ')');
        await client.leave();
        console.log('[AgoraMeetingProvider] Left Agora channel successfully');
      } else {
        console.log('[AgoraMeetingProvider] Client already disconnected, skipping leave()');
      }

      joinedRef.current = false;
      actualUidRef.current = null;
      setRemoteUsers(new Map());
      setRemoteUserMetadata(new Map());
      setMeetingState(AgoraConstants.meetingState.DISCONNECTED);
      callbacksRef.current.onMeetingLeft?.();
    } catch (error) {
      console.error('Failed to leave meeting:', error);
      // Even if leave() fails, reset local state to allow rejoin
      joinedRef.current = false;
      actualUidRef.current = null;
      setMeetingState(AgoraConstants.meetingState.DISCONNECTED);
    }
  }, [client, localAudioTrack, localVideoTrack, localScreenTrack, meetingId, participantId]);

  // End meeting for all
  const end = useCallback(async () => {
    await leave();
  }, [leave]);

  // Mute/unmute microphone - using setEnabled() pattern like VideoSDK
  // Returns { ok, verified, retried } so callers (ack-based remote mute, self-mute
  // verification) can detect silent failures on flaky mobile networks where
  // setEnabled() resolves but the underlying MediaStreamTrack keeps streaming.
  // Existing callers that ignore the return value continue to work unchanged.
  const muteMic = useCallback(async () => {
    console.log('[AgoraContext] muteMic called, localAudioTrack:', !!localAudioTrack);
    if (!localAudioTrack) {
      setIsMicOn(false);
      return { ok: true, verified: true, retried: false, reason: 'no-track' };
    }

    const verifyMuted = () => {
      try {
        const mediaTrack = localAudioTrack.getMediaStreamTrack?.();
        // enabled=false means the track is silenced at the OS layer.
        // If mediaTrack is missing, treat as verified (no track = no audio).
        return !mediaTrack || mediaTrack.enabled === false;
      } catch (_) {
        return false;
      }
    };

    let retried = false;
    try {
      await localAudioTrack.setEnabled(false);
      setIsMicOn(false);
      console.log('[AgoraContext] Mic muted via setEnabled(false)');

      // Verify the underlying MediaStreamTrack actually flipped. On flaky
      // mobile connections we've seen setEnabled resolve without the OS
      // flag actually changing, resulting in "muted UI but live audio".
      if (!verifyMuted()) {
        console.warn('[AgoraContext] muteMic verification failed, retrying once');
        retried = true;
        try {
          await localAudioTrack.setEnabled(false);
        } catch (retryErr) {
          console.warn('[AgoraContext] muteMic retry threw:', retryErr);
        }
      }

      return { ok: true, verified: verifyMuted(), retried };
    } catch (err) {
      console.error('[AgoraContext] Failed to disable audio track:', err);
      // Still update state — track may be in bad state. Caller can decide
      // whether to fall back to unsubscribing the remote track instead.
      setIsMicOn(false);
      return { ok: false, verified: verifyMuted(), retried, reason: err?.message || 'setEnabled-failed' };
    }
  }, [localAudioTrack]);

  const unmuteMic = useCallback(async (customAudioStream) => {
    console.log('[AgoraContext] unmuteMic called, localAudioTrack:', !!localAudioTrack);

    // Helper: build a fresh mic track + apply AI denoiser, publish, and store it.
    // Used both for first-time creation and for "zombie track" recovery after a
    // weak-network reconnect leaves the existing track orphaned/unpublishable.
    const createAndPublishNewTrack = async () => {
      console.log('[AgoraContext] Creating new audio track for unmute');
      let audioTrack;
      if (customAudioStream) {
        audioTrack = AgoraRTC.createCustomAudioTrack({ mediaStreamTrack: customAudioStream.getAudioTracks()[0] });
      } else {
        // Honor the user's selected mic on recovery — same reasoning as in join().
        const userMicId = getSelectedMicDeviceId();
        try {
          audioTrack = await AgoraRTC.createMicrophoneAudioTrack({
            microphoneId: userMicId,
            AEC: true,
            AGC: true,
            ANS: true,
          });
        } catch (deviceErr) {
          if (userMicId) {
            console.warn('[AgoraContext] unmuteMic device create failed for', userMicId, '— retrying with default:', deviceErr.message);
            const msg = describeDeviceError(deviceErr, 'mic');
            if (msg) { try { toast.info(msg); } catch (_) {} }
            audioTrack = await AgoraRTC.createMicrophoneAudioTrack({
              AEC: true, AGC: true, ANS: true,
            });
          } else {
            throw deviceErr;
          }
        }
      }

      // Apply AI noise cancellation if enabled (desktop only)
      if (noiseCancellationEnabledRef.current && !customAudioStream && !isMobileDevice()) {
        try {
          const ncSuccess = await enableNoiseCancellation(audioTrack, noiseCancellationModeRef.current, noiseCancellationLevelRef.current);
          console.log('[AgoraContext] AI noise cancellation applied on unmute:', ncSuccess);
        } catch (ncErr) {
          console.warn('[AgoraContext] Failed to apply AI noise cancellation:', ncErr);
        }
      }

      await client.publish(audioTrack);
      setLocalAudioTrack(audioTrack);
      setIsMicOn(true);
      console.log('[AgoraContext] New audio track created and published');
    };

    try {
      let trackToUse = localAudioTrack;

      // Step 1: Detect a fully-dead track (OS/browser killed the MediaStreamTrack
      // — happens on device disconnect, permission revoke, or some reconnects).
      if (trackToUse) {
        const mediaTrack = trackToUse.getMediaStreamTrack?.();
        if (!mediaTrack || mediaTrack.readyState === 'ended') {
          console.warn('[AgoraContext] Audio track exists but underlying MediaStreamTrack is dead, recreating');
          try { trackToUse.close(); } catch (e) { /* ignore */ }
          setLocalAudioTrack(null);
          trackToUse = null;
        }
      }

      // Step 2: Detect a "zombie" track — alive locally but no longer published
      // to the channel. This is the failure mode after a brief network drop:
      // Agora's auto-republish doesn't always re-attach existing tracks, so the
      // wrapper looks healthy but `setEnabled(true)` produces silence on the wire.
      // Try to republish; if that fails, fall through to full recreation.
      if (trackToUse && joinedRef.current && client) {
        const publishedLocalTracks = client.localTracks || [];
        const isPublished = publishedLocalTracks.includes(trackToUse);
        if (!isPublished) {
          console.warn('[AgoraContext] Audio track is not in client.localTracks (orphaned after reconnect), republishing');
          try {
            await client.publish(trackToUse);
            console.log('[AgoraContext] Successfully republished orphaned audio track');
            // After a reconnect republish, the denoiser singleton can have
            // drifted out of sync with the track pipeline. Re-verify so the
            // channel actually carries suppressed audio, not raw mic.
            if (noiseCancellationEnabledRef.current && !isMobileDevice()) {
              try {
                await verifyDenoiserAttached(
                  trackToUse,
                  noiseCancellationModeRef.current,
                  noiseCancellationLevelRef.current
                );
              } catch (ncErr) {
                console.warn('[AgoraContext] verifyDenoiserAttached after reconnect republish failed:', ncErr);
              }
            }
          } catch (republishErr) {
            console.warn('[AgoraContext] Republish failed, will recreate track:', republishErr);
            try { trackToUse.close(); } catch (e) { /* ignore */ }
            setLocalAudioTrack(null);
            trackToUse = null;
          }
        }
      }

      // Step 3: Enable the (now-known-good) track. If setEnabled itself throws,
      // the track is in an unrecoverable internal state — recreate from scratch.
      if (trackToUse) {
        try {
          await trackToUse.setEnabled(true);
          setIsMicOn(true);
          console.log('[AgoraContext] Mic unmuted via setEnabled(true)');
          return;
        } catch (enableErr) {
          console.warn('[AgoraContext] setEnabled(true) failed on existing track, recreating:', enableErr);
          try { trackToUse.close(); } catch (e) { /* ignore */ }
          setLocalAudioTrack(null);
          trackToUse = null;
        }
      }

      // Step 4: No usable track — create a fresh one and publish it.
      await createAndPublishNewTrack();
    } catch (error) {
      console.error('[AgoraContext] Failed to unmute mic:', error);
      // Last-ditch recovery: tear down whatever we have so the next click can
      // start clean. Then attempt one more fresh creation if we're still joined.
      if (localAudioTrackRef.current) {
        try { localAudioTrackRef.current.close(); } catch (e) { /* ignore */ }
        setLocalAudioTrack(null);
      }
      setIsMicOn(false);
      if (joinedRef.current && client) {
        try {
          await createAndPublishNewTrack();
        } catch (retryErr) {
          console.error('[AgoraContext] Retry after error also failed:', retryErr);
        }
      }
    }
  }, [client, localAudioTrack]);

  const toggleMic = useCallback(async () => {
    console.log('[AgoraContext] toggleMic called, current isMicOn:', isMicOn);
    if (isMicOn) {
      return await muteMic();
    } else {
      return await unmuteMic();
    }
  }, [isMicOn, muteMic, unmuteMic]);

  // Local-silence fallback: when a host mute command can't be verified via ack
  // (target offline/stale socket), each client can independently silence the
  // target in their own playback. Uses setVolume(0) so the track stays
  // subscribed — undoing the silencing is just setVolume(100).
  // Accepts either a numeric Agora UID or a string participantId; resolves it
  // against the participants map.
  const silenceRemoteUser = useCallback((participantOrUid) => {
    if (participantOrUid == null) return false;
    try {
      const target = remoteUsersRef.current?.get(participantOrUid)
        || remoteUsersRef.current?.get(Number(participantOrUid))
        || remoteUsersRef.current?.get(String(participantOrUid));
      const audioTrack = target?.audioTrack;
      if (audioTrack && typeof audioTrack.setVolume === 'function') {
        audioTrack.setVolume(0);
        console.log('[AgoraContext] silenceRemoteUser applied volume=0 to', participantOrUid);
        return true;
      }
      console.warn('[AgoraContext] silenceRemoteUser: no audioTrack found for', participantOrUid);
      return false;
    } catch (err) {
      console.warn('[AgoraContext] silenceRemoteUser failed:', err);
      return false;
    }
  }, []);

  const restoreRemoteUserVolume = useCallback((participantOrUid) => {
    if (participantOrUid == null) return false;
    try {
      const target = remoteUsersRef.current?.get(participantOrUid)
        || remoteUsersRef.current?.get(Number(participantOrUid))
        || remoteUsersRef.current?.get(String(participantOrUid));
      const audioTrack = target?.audioTrack;
      if (audioTrack && typeof audioTrack.setVolume === 'function') {
        audioTrack.setVolume(100);
        return true;
      }
      return false;
    } catch (_) {
      return false;
    }
  }, []);

  // Enable/disable webcam - using setEnabled() pattern like VideoSDK
  const disableWebcam = useCallback(async () => {
    console.log('[AgoraContext] disableWebcam called, localVideoTrack:', !!localVideoTrack);
    if (localVideoTrack) {
      // Fully close the track so the browser / OS releases the camera and the
      // hardware LED turns off. setEnabled(false) only pauses frames — the
      // camera stays acquired, leaving the indicator on. enableWebcam() is
      // already set up to create + publish a new track when localVideoTrack
      // is null, and the VB re-pipe effect in MeetingContainerNew will re-
      // apply the virtual background to the fresh track automatically.
      try { await client.unpublish(localVideoTrack); } catch (err) { console.warn('[AgoraContext] unpublish on disable failed:', err); }
      try { localVideoTrack.stop(); } catch (_) { /* ignore */ }
      try { localVideoTrack.close(); } catch (_) { /* ignore */ }
      setLocalVideoTrack(null);
      setIsWebcamOn(false);
      console.log('[AgoraContext] Webcam disabled — track closed, camera released');
    }
  }, [client, localVideoTrack]);

  const enableWebcam = useCallback(async (customVideoStream) => {
    console.log('[AgoraContext] enableWebcam called, localVideoTrack:', !!localVideoTrack);
    try {
      // Check if existing track's underlying MediaStreamTrack is still alive.
      // The browser/OS can kill it (device contention during screen share, permission revoke,
      // device disconnect). If it's dead, recreate from scratch — otherwise setEnabled(true)
      // succeeds silently but no actual video flows and the user can't get their camera back.
      // Mirrors the unmuteMic dead-track recovery pattern.
      let trackToUse = localVideoTrack;
      if (trackToUse && !customVideoStream) {
        const mediaTrack = trackToUse.getMediaStreamTrack?.();
        if (!mediaTrack || mediaTrack.readyState === 'ended') {
          console.warn('[AgoraContext] Video track exists but underlying MediaStreamTrack is dead, recreating');
          try { await client.unpublish(trackToUse); } catch (e) { /* ignore */ }
          try { trackToUse.close(); } catch (e) { /* ignore */ }
          setLocalVideoTrack(null);
          trackToUse = null; // Fall through to create a new track below
        }
      }

      if (trackToUse && !customVideoStream) {
        // Track exists and is alive, just enable it - matches VideoSDK behavior
        await trackToUse.setEnabled(true);
        setIsWebcamOn(true);
        console.log('[AgoraContext] Webcam enabled via setEnabled(true)');
      } else {
        // No track exists or custom stream provided, create and publish one
        console.log('[AgoraContext] Creating new video track for enable');

        // Release any lingering pre-join preview tracks (Chromium camera exclusivity)
        closePreviewVideoTrack();
        closePreviewAudioTrack();

        // If there's an existing track and we're replacing with custom stream, clean it up
        if (localVideoTrack && customVideoStream) {
          await client.unpublish(localVideoTrack);
          localVideoTrack.stop();
          localVideoTrack.close();
        }

        let videoTrack;
        if (customVideoStream) {
          videoTrack = AgoraRTC.createCustomVideoTrack({
            mediaStreamTrack: customVideoStream.getVideoTracks()[0],
            optimizationMode: 'motion',
          });
        } else {
          const totalParticipants = remoteUsers.size + 1;
          try {
            if (isMobileDevice()) {
              videoTrack = await createMobilePortraitTrack(getAdaptiveVideoProfile(totalParticipants));
            } else {
              videoTrack = await AgoraRTC.createCameraVideoTrack({
                encoderConfig: getAdaptiveVideoProfile(totalParticipants),
                optimizationMode: 'motion',
              });
            }
          } catch (trackErr) {
            console.warn('[AgoraContext] Camera track failed with profile, retrying with defaults:', trackErr.message || trackErr);
            videoTrack = await AgoraRTC.createCameraVideoTrack({ optimizationMode: 'motion' });
          }
        }
        setLocalVideoTrack(videoTrack);
        await client.publish(videoTrack);
        setIsWebcamOn(true);
        console.log('[AgoraContext] New video track created and published');
      }
    } catch (error) {
      console.error('[AgoraContext] Failed to enable webcam:', error);
    }
  }, [client, localVideoTrack]);

  const toggleWebcam = useCallback(async () => {
    console.log('[AgoraContext] toggleWebcam called, current isWebcamOn:', isWebcamOn);
    if (isWebcamOn) {
      await disableWebcam();
    } else {
      await enableWebcam();
    }
  }, [isWebcamOn, disableWebcam, enableWebcam]);

  // Change webcam (for virtual backgrounds etc)
  const changeWebcam = useCallback(async (newStream) => {
    try {
      // If a device ID string is passed, switch camera.
      // On mobile, custom tracks don't support setDevice(), so teardown and recreate.
      if (typeof newStream === 'string') {
        if (isMobileDevice()) {
          if (_switchingCamera) return;
          _switchingCamera = true;
          try {
            if (localVideoTrack) {
              await client.unpublish(localVideoTrack);
              localVideoTrack.stop();
              localVideoTrack.close();
            }
            const totalParticipants = remoteUsers.size + 1;
            const videoTrack = await createMobilePortraitTrack(
              getAdaptiveVideoProfile(totalParticipants),
              newStream
            );
            setLocalVideoTrack(videoTrack);
            await client.publish(videoTrack);
            setIsWebcamOn(true);
          } finally {
            _switchingCamera = false;
          }
        } else if (localVideoTrack && localVideoTrack.setDevice) {
          await localVideoTrack.setDevice(newStream);
        }
        return;
      }

      if (localVideoTrack) {
        await client.unpublish(localVideoTrack);
        localVideoTrack.stop();
        localVideoTrack.close();
      }

      let videoTrack;
      if (newStream instanceof MediaStreamTrack) {
        videoTrack = AgoraRTC.createCustomVideoTrack({
          mediaStreamTrack: newStream,
          optimizationMode: 'motion',
        });
      } else if (newStream instanceof MediaStream) {
        videoTrack = AgoraRTC.createCustomVideoTrack({
          mediaStreamTrack: newStream.getVideoTracks()[0],
          optimizationMode: 'motion',
        });
      } else {
        videoTrack = newStream; // Assume it's already an Agora track
      }

      setLocalVideoTrack(videoTrack);
      await client.publish(videoTrack);
      setIsWebcamOn(true);
    } catch (error) {
      console.error('Failed to change webcam:', error);
    }
  }, [client, localVideoTrack, remoteUsers]);

  // Screen sharing - uses separate screenClient with different UID
  // Ref for disableScreenShare to avoid closure issues
  const disableScreenShareRef = useRef(null);
  const screenClientJoinedRef = useRef(false);

  const disableScreenShare = useCallback(async () => {
    try {
      console.log('[AgoraContext] disableScreenShare called, screenClient state:', screenClient?.connectionState);
      // Stop and close local screen tracks
      if (localScreenTrack) {
        localScreenTrack.stop();
        localScreenTrack.close();
        setLocalScreenTrack(null);
      }
      // Stop the cloned raw capture track used for PiP
      if (screenCaptureTrackRef.current) {
        try { screenCaptureTrackRef.current.stop(); } catch (_) { }
        screenCaptureTrackRef.current = null;
      }
      if (localScreenAudioTrack) {
        localScreenAudioTrack.stop();
        localScreenAudioTrack.close();
        setLocalScreenAudioTrack(null);
      }

      // Leave channel on screen client
      if (screenClientJoinedRef.current) {
        await screenClient.leave();
        screenClientJoinedRef.current = false;
      }

      // Recover the main client's mic track if screen share disturbed it.
      // The screen-share start path already guards against the mic being paused
      // by the display-capture pipeline (see enableScreenShare). The stop path
      // needs the equivalent: after screenClient.leave(), the main client's
      // localAudioTrack can be left orphaned (alive but no longer in
      // client.localTracks) or dead. If we don't recover here, remote peers
      // stop hearing the host AND mute/unmute silently no-ops because the
      // handlers keep targeting the stale ref. Mirrors the reconnection
      // recovery in handleConnectionStateChange (~line 1925).
      try {
        if (localAudioTrack) {
          const mediaTrack = localAudioTrack.getMediaStreamTrack?.();
          const isDead = !mediaTrack || mediaTrack.readyState === 'ended';
          const publishedLocalTracks = clientRef.current?.localTracks || [];
          const isOrphaned = !isDead && !publishedLocalTracks.includes(localAudioTrack);

          if (isDead) {
            console.warn('[AgoraContext] Audio track dead after screen share stop, cleaning up');
            try { localAudioTrack.close(); } catch (e) { /* ignore */ }
            setLocalAudioTrack(null);
            setIsMicOn(false);
          } else if (isOrphaned) {
            console.warn('[AgoraContext] Audio track orphaned after screen share stop, republishing');
            try {
              await clientRef.current.publish(localAudioTrack);
              console.log('[AgoraContext] Re-published audio track after screen share stop');
            } catch (err) {
              console.warn('[AgoraContext] Republish after screen share stop failed, dropping track:', err);
              try { localAudioTrack.close(); } catch (e) { /* ignore */ }
              setLocalAudioTrack(null);
              setIsMicOn(false);
            }
          } else {
            // Track is healthy and published — defensively re-assert its
            // enabled state in case a browser paused the mic during capture
            // (matches the mic-recovery pattern in enableScreenShare).
            try {
              await localAudioTrack.setEnabled(localAudioTrack.enabled);
            } catch (err) {
              console.warn('[AgoraContext] setEnabled re-assert after screen share stop failed:', err);
            }
          }
        }
      } catch (recoveryErr) {
        console.warn('[AgoraContext] Mic recovery after screen share stop failed:', recoveryErr);
      }

      setIsScreenSharing(false);
      setPresenterId(null);
      callbacksRef.current.onPresenterChanged?.(null);

      // Notify backend that screen share stopped
      if (meetingId) {
        try {
          const numericUid = stringToNumericUid(participantId);
          const screenShareUid = getScreenShareUid(numericUid);
          await axios.post(`${process.env.REACT_APP_BASE_URL}/agora/screen-share/stop`, {
            meetingId,
            participantId: String(screenShareUid),
          });
        } catch (err) {
          console.error('Failed to notify screen share stop:', err);
        }
      }
    } catch (error) {
      console.error('Failed to disable screen share:', error);
    }
  }, [screenClient, localScreenTrack, localScreenAudioTrack, meetingId, participantId]);

  // Keep the ref updated
  useEffect(() => {
    disableScreenShareRef.current = disableScreenShare;
  }, [disableScreenShare]);

  const enableScreenShare = useCallback(async () => {
    // Guard: check if browser supports screen sharing at all
    if (!navigator.mediaDevices?.getDisplayMedia) {
      toast.error("Screen sharing is not supported in this browser. Please use Chrome, Edge, or Safari 13+.", {
        position: "bottom-left", autoClose: 5000, theme: "dark",
      });
      return;
    }

    let origGetDisplayMedia = null;
    try {
      // Intercept getDisplayMedia to capture the raw browser screen stream for PiP
      // Agora's createScreenVideoTrack calls getDisplayMedia internally — we hook it
      // to grab the original MediaStream before Agora processes/wraps it
      //
      // Skip on Safari: Safari is strict about user gesture propagation — wrapping
      // getDisplayMedia in an async function breaks the gesture chain, causing Safari
      // to silently block the screen capture request.
      let capturedRawClone = null;
      if (!isSafari) {
        origGetDisplayMedia = navigator.mediaDevices.getDisplayMedia.bind(navigator.mediaDevices);
        navigator.mediaDevices.getDisplayMedia = async function (...args) {
          const stream = await origGetDisplayMedia(...args);
          // CLONE the video track before Agora takes ownership
          // Agora may stop/replace the original — our clone stays alive independently
          const videoTrackRaw = stream.getVideoTracks()[0];
          if (videoTrackRaw) {
            capturedRawClone = videoTrackRaw.clone();
            console.log('[AgoraContext] Cloned raw screen capture for PiP, label:', capturedRawClone.label, 'readyState:', capturedRawClone.readyState);
          }
          return stream;
        };
      }

      // Create screen tracks
      // Use standard 1080p (not 1080p_2) to reduce bandwidth pressure
      // so that microphone audio has enough bandwidth and doesn't break
      const screenTracks = await AgoraRTC.createScreenVideoTrack({
        encoderConfig: '1080p'
      }, 'auto');

      // Restore original getDisplayMedia (only if we patched it)
      if (origGetDisplayMedia) {
        navigator.mediaDevices.getDisplayMedia = origGetDisplayMedia;
      }

      let videoTrack, audioTrack;
      if (Array.isArray(screenTracks)) {
        [videoTrack, audioTrack] = screenTracks;
        setLocalScreenAudioTrack(audioTrack);
      } else {
        videoTrack = screenTracks;
      }

      setLocalScreenTrack(videoTrack);

      // Store the CLONED raw screen capture track for PiP
      // The clone is independent of Agora's processing — it stays alive and shows actual screen content
      if (capturedRawClone) {
        screenCaptureTrackRef.current = capturedRawClone;
        console.log('[AgoraContext] Cloned screen capture stored for PiP, label:', capturedRawClone.label, 'readyState:', capturedRawClone.readyState);
      }

      // Use separate UID for screen share (main UID + 1)
      const numericUid = stringToNumericUid(participantId);
      const screenShareUid = getScreenShareUid(numericUid);

      // Get token for screen share UID
      const screenToken = await getAgoraToken(meetingId, screenShareUid);

      // Join channel with screen client using screen share UID
      await screenClient.join(
        process.env.REACT_APP_AGORA_APP_ID,
        meetingId,
        screenToken,
        screenShareUid
      );
      screenClientJoinedRef.current = true;

      // Publish screen track via screen client
      await screenClient.publish(audioTrack ? [videoTrack, audioTrack] : videoTrack);
      setIsScreenSharing(true);

      // Use the main participantId as presenter ID (not screen share UID)
      // This is because participants are stored by their main ID in the participants map
      setPresenterId(participantId);
      callbacksRef.current.onPresenterChanged?.(participantId);

      // Notify backend that screen share started
      if (meetingId) {
        try {
          await axios.post(`${process.env.REACT_APP_BASE_URL}/agora/screen-share/start`, {
            meetingId,
            participantId: String(screenShareUid),
            mainParticipantId: String(numericUid), // Also send main UID for reference
          });
        } catch (err) {
          console.error('Failed to notify screen share start:', err);
        }
      }

      // Recover mic track — some browsers pause the mic when screen share
      // captures audio. Only re-enable if the user INTENDED the mic to be on
      // (isMicOn). Otherwise a user who muted before sharing would find
      // themselves silently unmuted to the room while their own UI still
      // shows muted. Read intent via the ref to avoid a stale closure.
      if (localAudioTrack && isMicOnRef.current) {
        setTimeout(async () => {
          try {
            if (!isMicOnRef.current) return;
            if (!localAudioTrack.enabled) {
              await localAudioTrack.setEnabled(true);
              console.log('[AgoraContext] Re-enabled mic track after screen share start');
            }
          } catch (err) {
            console.warn('[AgoraContext] Mic recovery after screen share failed:', err);
          }
        }, 500);
      }

      // DEBUG: Monitor the underlying MediaStreamTrack to catch what stops it
      const mediaTrack = videoTrack.getMediaStreamTrack?.();
      if (mediaTrack) {
        const origStop = mediaTrack.stop.bind(mediaTrack);
        mediaTrack.stop = function () {
          console.error('[AgoraContext-DEBUG] MediaStreamTrack.stop() called! Stack:', new Error().stack);
          return origStop();
        };
        // Monitor readyState changes
        const trackMonitor = setInterval(() => {
          if (mediaTrack.readyState === 'ended') {
            console.error('[AgoraContext-DEBUG] MediaStreamTrack readyState became "ended"! muted:', mediaTrack.muted);
            clearInterval(trackMonitor);
          }
        }, 2000);
        // Also patch the Agora track's stop/close
        const origTrackStop = videoTrack.stop?.bind(videoTrack);
        const origTrackClose = videoTrack.close?.bind(videoTrack);
        if (origTrackStop) {
          videoTrack.stop = function () {
            console.error('[AgoraContext-DEBUG] Agora videoTrack.stop() called! Stack:', new Error().stack);
            return origTrackStop();
          };
        }
        if (origTrackClose) {
          videoTrack.close = function () {
            console.error('[AgoraContext-DEBUG] Agora videoTrack.close() called! Stack:', new Error().stack);
            return origTrackClose();
          };
        }
      }

      // Handle browser stop share button - use ref to get latest function
      videoTrack.on('track-ended', () => {
        console.warn('[AgoraContext] Screen share track-ended event fired');
        // Verify the underlying MediaStreamTrack is actually ended before killing share.
        // Browser can fire track-ended on display sleep, tab backgrounding, or OS permission changes
        // that are temporary — only stop if the track is truly dead.
        const mediaTrack = videoTrack.getMediaStreamTrack?.();
        const trackState = mediaTrack?.readyState;
        if (trackState === 'ended') {
          console.warn('[AgoraContext] Screen share track confirmed ended — stopping screen share');
          disableScreenShareRef.current?.();
        } else {
          console.log(`[AgoraContext] Screen share track-ended fired but track readyState=${trackState} — ignoring (may recover)`);
          // Re-check after a short delay in case the track truly ended with a delay
          setTimeout(() => {
            const recheckState = videoTrack.getMediaStreamTrack?.()?.readyState;
            if (recheckState === 'ended' && isScreenSharingRef.current) {
              console.warn('[AgoraContext] Screen share track confirmed ended after recheck — stopping');
              disableScreenShareRef.current?.();
            }
          }, 3000);
        }
      });

      // Handle token expiry on screen client — retry with exponential backoff
      screenClient.on('token-privilege-will-expire', async () => {
        console.log('[AgoraContext] Screen client token expiring, renewing...');
        const maxRetries = 3;
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            const newToken = await getAgoraToken(meetingId, screenShareUid);
            if (newToken && screenClientJoinedRef.current) {
              await screenClient.renewToken(newToken);
              console.log('[AgoraContext] Screen client token renewed successfully');
              return; // Success — exit retry loop
            }
          } catch (err) {
            console.error(`[AgoraContext] Screen client token renewal failed (attempt ${attempt}/${maxRetries}):`, err);
            if (attempt < maxRetries) {
              const delay = Math.min(1000 * Math.pow(2, attempt - 1), 8000); // 1s, 2s, 4s
              console.log(`[AgoraContext] Retrying token renewal in ${delay}ms...`);
              await new Promise(resolve => setTimeout(resolve, delay));
            }
          }
        }
        console.error('[AgoraContext] Screen client token renewal exhausted all retries — screen share may disconnect');
      });
    } catch (error) {
      console.error('Failed to enable screen share:', error);
      // Restore getDisplayMedia if it was patched
      if (origGetDisplayMedia) {
        navigator.mediaDevices.getDisplayMedia = origGetDisplayMedia;
      }
      // Show user-facing error for real failures (not user cancellation)
      const errName = error?.name || '';
      const errCode = error?.code || '';
      const isPermissionDenied = errName === 'NotAllowedError' || errCode === 'PERMISSION_DENIED';
      if (isPermissionDenied) {
        // Permission denied — covers both "Don't Allow" click and Safari's
        // cached-deny on subsequent clicks (where the picker no longer
        // appears). Browser security prevents re-prompting from JS, so guide
        // the user to the recovery path.
        const settingsHint = isSafari
          ? "Hard refresh the page and click Allow, or enable it in Safari → Settings → Websites → Screen Sharing."
          : "Hard refresh the page and click Allow, or enable screen sharing in your browser's site settings.";
        toast.error(`Screen sharing is blocked by your browser. ${settingsHint}`, {
          position: "bottom-left", autoClose: 7000, theme: "dark",
        });
      } else if (errName === 'AbortError') {
        // User dismissed the picker without choosing — intentional, no toast
      } else if (errName === 'NotSupportedError') {
        toast.error("Screen sharing is not supported in this browser.", {
          position: "bottom-left", autoClose: 5000, theme: "dark",
        });
      } else if (errName === 'NotReadableError') {
        toast.error("Could not access screen. Please check your system screen recording permissions.", {
          position: "bottom-left", autoClose: 5000, theme: "dark",
        });
      } else {
        toast.error("Screen sharing failed. Please try again or use a different browser.", {
          position: "bottom-left", autoClose: 5000, theme: "dark",
        });
      }
    }
  }, [screenClient, participantId, meetingId, localAudioTrack, isSafari]);

  const toggleScreenShare = useCallback(async () => {
    if (isScreenSharing) {
      await disableScreenShare();
    } else {
      await enableScreenShare();
    }
  }, [isScreenSharing, enableScreenShare, disableScreenShare]);

  // Track screen share UIDs (separate from main user UIDs)
  const screenShareUidsRef = useRef(new Set());

  // Effect to reclassify misidentified screen shares when presenterId is set from backend polling
  // This handles the case where a late joiner receives screen share track before the polling detects it
  useEffect(() => {
    if (!presenterId) return;

    const expectedScreenShareUid = getScreenShareUid(presenterId);

    // Check if the screen share was misclassified as a regular user
    if (remoteUsers.has(expectedScreenShareUid) && !remoteScreenShares.has(expectedScreenShareUid)) {
      console.log('[AgoraContext] Reclassifying misidentified screen share:', expectedScreenShareUid, 'for presenter:', presenterId);

      const user = remoteUsers.get(expectedScreenShareUid);

      // Add to screen share UIDs set
      screenShareUidsRef.current.add(expectedScreenShareUid);

      // Move to remoteScreenShares
      setRemoteScreenShares(prev => {
        const newMap = new Map(prev);
        newMap.set(expectedScreenShareUid, {
          uid: expectedScreenShareUid,
          mainUid: presenterId,
          videoTrack: user.videoTrack,
          audioTrack: user.audioTrack,
        });
        return newMap;
      });

      // Remove from remoteUsers
      setRemoteUsers(prev => {
        const newMap = new Map(prev);
        newMap.delete(expectedScreenShareUid);
        return newMap;
      });
    }
  }, [presenterId, remoteUsers, remoteScreenShares]);

  // Handle remote user events
  useEffect(() => {
    // Helper to check if a UID is likely a screen share UID
    // Screen share UID = main UID + 1, so if (UID - 1) exists in remoteUsers, this is a screen share
    // Also check if it's already tracked as a screen share UID
    const isLikelyScreenShareUid = (uid) => {
      // Already tracked as screen share
      if (screenShareUidsRef.current.has(uid)) {
        return true;
      }

      const mainUid = getMainUidFromScreenShare(uid);

      // Check if the main UID exists in remoteUsers
      if (remoteUsersRef.current?.has(mainUid)) {
        return true;
      }

      // Check if this UID matches the expected screen share UID for the current presenter
      // This handles late joiners when presenter is already detected via backend polling
      const currentPresenterId = presenterIdRef.current;
      if (currentPresenterId) {
        const expectedScreenShareUid = getScreenShareUid(currentPresenterId);
        if (uid === expectedScreenShareUid) {
          return true;
        }
      }

      // Additional heuristic: screen share UIDs are typically odd numbers
      // (since they're main UID + 1, and main UIDs from stringToNumericUid are typically even)
      // Check if (uid - 1) would be a valid main UID pattern
      // This helps catch screen shares that join before main user
      const potentialMainUid = mainUid;

      // If we have metadata for the potential main UID, this is likely a screen share
      if (remoteUserMetadataRef.current?.has(potentialMainUid) ||
        remoteUserMetadataRef.current?.has(String(potentialMainUid))) {
        return true;
      }

      // Check if this UID is exactly 1 more than any known remote user UID
      // This catches screen shares that join very quickly after the main user
      for (const knownUid of remoteUsersRef.current?.keys() || []) {
        if (uid === knownUid + 1) {
          console.log('Detected screen share UID via +1 heuristic:', uid, 'main:', knownUid);
          return true;
        }
      }

      // Check if (uid - 1) matches our own local participant ID's numeric form
      // This catches when we share our own screen and receive it back
      const localNumericUid = stringToNumericUid(participantId);
      if (uid === localNumericUid + 1) {
        console.log('Detected local screen share UID:', uid);
        return true;
      }

      // Check Agora client's native remote users list (always up-to-date, unlike React state)
      // This is critical on mobile where React state updates may lag behind Agora SDK events
      const agoraRemoteUsers = client?.remoteUsers || [];
      for (const remoteUser of agoraRemoteUsers) {
        if (uid === remoteUser.uid + 1) {
          console.log('Detected screen share UID via Agora client +1 heuristic:', uid, 'main:', remoteUser.uid);
          return true;
        }
      }

      return false;
    };

    const handleUserPublished = async (user, mediaType) => {
      // SAFETY CHECK: Skip if this is somehow the local user's UID
      // This shouldn't happen normally, but prevents issues if Agora sends self-events
      const localNumericUid = stringToNumericUid(participantId);
      if (user.uid === localNumericUid) {
        console.log('[AgoraContext] Skipping user-published for local user UID:', user.uid);
        return;
      }

      await client.subscribe(user, mediaType);
      console.log('User published:', user.uid, mediaType);

      // Play remote audio using Agora's native playback (handles jitter buffer, auto-recovery)
      if (mediaType === 'audio' && user.audioTrack) {
        try {
          // Diagnostic: track how many times audio play is called per uid.
          // In grid-recorder mode, the shared AgoraProvider should fire this
          // ONCE per participant. If we see repeat play() calls without
          // matching stop() calls in between, Chrome re-routes the audio
          // pipeline each time → audible stutter. Recorder worker pipes
          // [BrowserConsole:grid] logs so we can spot the pattern.
          user.audioTrack.play();
          console.log('[AgoraContext] Remote audio play() uid=' + user.uid + ' at ' + Date.now());
          // Route this track to the user-selected speaker device, if one is set.
          // setSinkId() on DOM elements does NOT affect Agora's internally-rendered
          // remote audio — only setPlaybackDevice() on the track does.
          if (playbackDeviceIdRef.current && typeof user.audioTrack.setPlaybackDevice === 'function') {
            user.audioTrack.setPlaybackDevice(playbackDeviceIdRef.current).catch(err => {
              console.warn('[AgoraContext] setPlaybackDevice on new remote track failed:', err);
            });
          }
        } catch (audioPlayErr) {
          console.warn('[AgoraContext] Failed to play remote audio via Agora:', audioPlayErr);
        }
      }

      // Check if this is a screen share UID (must check BEFORE setting fallback)
      const isScreenShare = screenShareUidsRef.current.has(user.uid) || isLikelyScreenShareUid(user.uid);

      // Set stream fallback: when bandwidth is constrained, degrade to low-quality video.
      // Use option 1 (VIDEO_STREAM_LOW) instead of 2 (audio-only) because option 2
      // drops the video track entirely — if a screen share UID is misidentified as a
      // regular user (race condition), this causes the screen share to disappear after
      // a few minutes when bandwidth fluctuates.
      // Skip for screen share streams entirely.
      // Skip for recorder mode — the droplet has a strong, stable connection
      // and we want HIGH quality (720p) video captured at all times.
      const isRecorderPage = typeof window !== 'undefined' &&
        new URLSearchParams(window.location.search).get('recorder') === 'true';

      if (!isScreenShare && !isRecorderPage) {
        try {
          await client.setStreamFallbackOption(user.uid, 1);
        } catch (e) {
          // Not critical — some user states don't support this
        }
      }

      // Recorder: force HIGH stream (720p, streamType=0). Without this, Agora
      // may deliver the low 160p stream by default for remote subs, and since
      // the recorder is just another remote subscriber, cropped 1280x720 tiles
      // end up upscaled from 160p — that's the "low video quality" users see.
      if (!isScreenShare && isRecorderPage && mediaType === 'video') {
        try {
          await client.setRemoteVideoStreamType(user.uid, 0);
          console.log('[AgoraContext] Recorder forced HIGH stream for uid=' + user.uid);
        } catch (e) {
          console.warn('[AgoraContext] setRemoteVideoStreamType HIGH failed:', e?.message);
        }
      }

      if (isScreenShare) {
        // Route ALL media types from screen share UIDs to remoteScreenShares
        // (not just video — screen audio was previously falling into remoteUsers as a phantom participant)
        screenShareUidsRef.current.add(user.uid);

        if (mediaType === 'video') {
          console.log('Screen share VIDEO track received from:', user.uid);
          const mainUid = getMainUidFromScreenShare(user.uid);

          // Set presenter to the MAIN user's UID (how they're stored in participants)
          setPresenterId(mainUid);
          callbacksRef.current.onPresenterChanged?.(mainUid);

          setRemoteScreenShares(prev => {
            const newMap = new Map(prev);
            newMap.set(user.uid, {
              uid: user.uid,
              mainUid: mainUid,
              videoTrack: user.videoTrack,
              audioTrack: user.audioTrack,
            });
            return newMap;
          });
        } else if (mediaType === 'audio') {
          // Screen share audio — attach to existing screen share entry
          console.log('Screen share AUDIO track received from:', user.uid);
          // Route screen-share audio through the user-selected speaker, if any.
          if (user.audioTrack && playbackDeviceIdRef.current && typeof user.audioTrack.setPlaybackDevice === 'function') {
            user.audioTrack.setPlaybackDevice(playbackDeviceIdRef.current).catch(err => {
              console.warn('[AgoraContext] setPlaybackDevice on screen-share audio failed:', err);
            });
          }
          setRemoteScreenShares(prev => {
            const newMap = new Map(prev);
            const existing = newMap.get(user.uid);
            if (existing) {
              newMap.set(user.uid, { ...existing, audioTrack: user.audioTrack });
            } else {
              // Audio arrived before video — store it, video will merge later
              const mainUid = getMainUidFromScreenShare(user.uid);
              newMap.set(user.uid, { uid: user.uid, mainUid, audioTrack: user.audioTrack, videoTrack: null });
            }
            return newMap;
          });
        }
        return; // Don't add screen share UIDs to remoteUsers
      } else {
        // Regular webcam/audio track
        setRemoteUsers(prev => {
          const newMap = new Map(prev);
          const existing = newMap.get(user.uid) || { uid: user.uid };
          newMap.set(user.uid, {
            ...existing,
            [mediaType + 'Track']: user[mediaType + 'Track'],
          });
          return newMap;
        });
      }
    };

    const handleUserUnpublished = (user, mediaType) => {
      console.log('User unpublished:', user.uid, mediaType);

      // Check if this was a screen share
      if (screenShareUidsRef.current.has(user.uid)) {
        setRemoteScreenShares(prev => {
          const newMap = new Map(prev);
          newMap.delete(user.uid);
          return newMap;
        });
        // Clear presenter - use string comparison to handle type mismatches
        const mainUid = getMainUidFromScreenShare(user.uid);
        const currentPresenterId = presenterIdRef.current;
        if (currentPresenterId && String(currentPresenterId) === String(mainUid)) {
          console.log('Clearing presenter after screen share unpublished:', mainUid);
          setPresenterId(null);
          callbacksRef.current.onPresenterChanged?.(null);
        }
      } else {
        setRemoteUsers(prev => {
          const newMap = new Map(prev);
          const existing = newMap.get(user.uid);
          if (existing) {
            newMap.set(user.uid, {
              ...existing,
              [mediaType + 'Track']: null,
            });
          }
          return newMap;
        });
      }
    };

    const handleUserJoined = (user) => {
      console.log('User joined:', user.uid);

      // SAFETY CHECK: Skip if this is somehow the local user's UID
      // This shouldn't happen normally, but prevents issues if Agora sends self-events
      const localNumericUid = stringToNumericUid(participantId);
      if (user.uid === localNumericUid) {
        console.log('[AgoraContext] Skipping user-joined for local user UID:', user.uid);
        return;
      }

      // Skip recorder bot UIDs always; STT bot UIDs only when show-bot-in-participants is off.
      // Worker-recorder launches multiple browsers (one per layout) with UIDs in the
      // 999999990-999999999 range; use range check so all of them are hidden.
      if (user.uid >= 999999990 && user.uid <= 999999999) {
        console.log('[AgoraContext] Skipping recorder UID:', user.uid);
        return;
      }
      if ((user.uid >= 999000 && user.uid <= 999999) && !showSttBotRef.current) {
        console.log('[AgoraContext] Skipping STT bot UID (hidden):', user.uid);
        return;
      }

      // Skip recorder bot by checking if this UID matches the recorder URL params
      // (handles case where recorder joins with its URL UID)
      const recorderUrlUid = new URLSearchParams(window.location.search).get('uid');
      if (recorderUrlUid && user.uid === parseInt(recorderUrlUid)) {
        console.log('[AgoraContext] Skipping recorder URL UID:', user.uid);
        return;
      }

      // Check if this is likely a screen share UID
      const isScreenShare = isLikelyScreenShareUid(user.uid);

      if (isScreenShare) {
        // This is a screen share UID - don't add to remoteUsers
        console.log('Screen share user joined:', user.uid);
        screenShareUidsRef.current.add(user.uid);
        return;
      }

      // Regular user
      setRemoteUsers(prev => {
        const newMap = new Map(prev);
        if (!newMap.has(user.uid)) {
          newMap.set(user.uid, { uid: user.uid });
        }
        return newMap;
      });

      // Fetch participant info when a new user joins — retry to give them time to register
      if (fetchParticipantInfoRef.current) {
        setTimeout(() => fetchParticipantInfoRef.current?.(), 500);
        setTimeout(() => fetchParticipantInfoRef.current?.(), 1500);
        setTimeout(() => fetchParticipantInfoRef.current?.(), 3000);
      }

      callbacksRef.current.onParticipantJoined?.({
        participantId: user.uid,
        displayName: `Participant ${user.uid}`,
      });
    };

    const handleUserLeft = (user) => {
      console.log('User left:', user.uid);

      // Check if it was a screen share user
      if (screenShareUidsRef.current.has(user.uid)) {
        screenShareUidsRef.current.delete(user.uid);
        setRemoteScreenShares(prev => {
          const newMap = new Map(prev);
          newMap.delete(user.uid);
          return newMap;
        });
        // IMPORTANT: Also remove from remoteUsers in case it was mistakenly added there
        setRemoteUsers(prev => {
          const newMap = new Map(prev);
          newMap.delete(user.uid);
          return newMap;
        });
        // Also clean up metadata
        setRemoteUserMetadata(prev => {
          const newMap = new Map(prev);
          newMap.delete(user.uid);
          newMap.delete(String(user.uid));
          return newMap;
        });
        // Clear presenter - use string comparison to handle type mismatches
        const mainUid = getMainUidFromScreenShare(user.uid);
        const currentPresenterId = presenterIdRef.current;
        // Compare as strings to handle both string and numeric IDs
        if (currentPresenterId && String(currentPresenterId) === String(mainUid)) {
          console.log('Clearing presenter after screen share user left:', mainUid);
          setPresenterId(null);
          callbacksRef.current.onPresenterChanged?.(null);
        }
      } else {
        // Regular user left - also check if they had a screen share and clean it up
        const userScreenShareUid = getScreenShareUid(user.uid);
        if (screenShareUidsRef.current.has(userScreenShareUid)) {
          console.log('Cleaning up screen share for user who left:', user.uid, 'screenShareUid:', userScreenShareUid);
          screenShareUidsRef.current.delete(userScreenShareUid);
          setRemoteScreenShares(prev => {
            const newMap = new Map(prev);
            newMap.delete(userScreenShareUid);
            return newMap;
          });
          // Clear presenter if it was this user - use string comparison
          const currentPresenterId = presenterIdRef.current;
          if (currentPresenterId && String(currentPresenterId) === String(user.uid)) {
            console.log('Clearing presenter after user with screen share left:', user.uid);
            setPresenterId(null);
            callbacksRef.current.onPresenterChanged?.(null);
          }
        }

        // Also clear presenter if the main user ID matches (even if screen share UID wasn't tracked)
        // This handles edge cases where the screen share cleanup may have failed
        const currentPresenterId = presenterIdRef.current;
        if (currentPresenterId && String(currentPresenterId) === String(user.uid)) {
          console.log('Clearing presenter - main user left:', user.uid);
          setPresenterId(null);
          callbacksRef.current.onPresenterChanged?.(null);
        }

        setRemoteUsers(prev => {
          const newMap = new Map(prev);
          newMap.delete(user.uid);
          // IMPORTANT: Also delete the user's screen share UID from remoteUsers
          // This handles cases where screen share UID was mistakenly added to remoteUsers
          newMap.delete(userScreenShareUid);
          return newMap;
        });

        // Clean up remoteUserMetadata for this user (and their screen share UID)
        setRemoteUserMetadata(prev => {
          const newMap = new Map(prev);
          newMap.delete(user.uid);
          newMap.delete(String(user.uid));
          // Also clean up screen share UID metadata
          newMap.delete(userScreenShareUid);
          newMap.delete(String(userScreenShareUid));
          return newMap;
        });

        // Notify backend to clean up participant info
        if (meetingId) {
          axios.delete(
            `${process.env.REACT_APP_BASE_URL}/agora/participant-info/${meetingId}/${user.uid}`
          ).catch(err => console.error('Failed to cleanup participant info:', err));
        }

        // Auto-unpin if this participant was spotlighted
        setPinnedParticipants(prev => {
          if (prev.has(user.uid) || prev.has(String(user.uid))) {
            const newMap = new Map(prev);
            newMap.delete(user.uid);
            newMap.delete(String(user.uid));
            if (typeof user.uid === 'number') newMap.delete(user.uid);
            console.log('[AgoraContext] Auto-unpinned departed participant:', user.uid);
            return newMap;
          }
          return prev;
        });

        callbacksRef.current.onParticipantLeft?.({
          participantId: user.uid,
        });
      }
    };

    const handleConnectionStateChange = (curState, prevState) => {
      console.log('Connection state changed:', prevState, '->', curState);
      if (curState === 'RECONNECTING') {
        setMeetingState(AgoraConstants.meetingState.RECONNECTING);
        callbacksRef.current.onMeetingStateChanged?.({ state: 'RECONNECTING' });
      } else if (curState === 'CONNECTED') {
        setMeetingState(AgoraConstants.meetingState.CONNECTED);
        callbacksRef.current.onMeetingStateChanged?.({ state: 'CONNECTED' });

        // After reconnection, verify local audio track is still alive AND still
        // published. Network disruption can either (a) kill the underlying
        // MediaStreamTrack, or (b) leave the wrapper alive but silently drop it
        // from client.localTracks — both states make the mic toggle a no-op
        // until we recover the track.
        if (prevState === 'RECONNECTING') {
          const audioTrack = localAudioTrackRef.current;
          if (audioTrack) {
            const mediaTrack = audioTrack.getMediaStreamTrack?.();
            const isDead = !mediaTrack || mediaTrack.readyState === 'ended';
            const publishedLocalTracks = clientRef.current?.localTracks || [];
            const isOrphaned = !isDead && !publishedLocalTracks.includes(audioTrack);

            if (isDead) {
              console.warn('[AgoraContext] Audio track dead after reconnection, cleaning up');
              try { audioTrack.close(); } catch (e) { /* ignore */ }
              setLocalAudioTrack(null);
              setIsMicOn(false);
            } else if (isOrphaned) {
              // Track is alive but Agora dropped it from the channel. Try to
              // republish so the mic toggle keeps working without the user
              // having to click anything. (Republishing preserves the muted
              // state — setEnabled(false) tracks stay silent on the wire.)
              // If republish fails the next unmuteMic() call will fall through
              // to full recreation.
              console.warn('[AgoraContext] Audio track orphaned after reconnection, republishing');
              clientRef.current.publish(audioTrack).then(() => {
                console.log('[AgoraContext] Re-published audio track after reconnection');
              }).catch((err) => {
                console.warn('[AgoraContext] Republish after reconnect failed, dropping track:', err);
                try { audioTrack.close(); } catch (e) { /* ignore */ }
                setLocalAudioTrack(null);
                setIsMicOn(false);
              });
            }
          }
        }
      } else if (curState === 'DISCONNECTED') {
        // Clean up remote users and screen shares to prevent React render errors
        // from components trying to access destroyed tracks
        console.log('[AgoraContext] DISCONNECTED - cleaning up remote users and screen shares');
        setRemoteUsers(new Map());
        setRemoteScreenShares(new Map());
        setRemoteUserMetadata(new Map());
        screenShareUidsRef.current.clear();
        setPresenterId(null);
        setMeetingState(AgoraConstants.meetingState.DISCONNECTED);
        callbacksRef.current.onMeetingStateChanged?.({ state: 'DISCONNECTED' });
      }
    };

    // Handle volume indicator for active speaker detection
    // Use a ref to track the last active speaker and implement holdover
    let lastActiveSpeaker = null;
    let lastInstantSpeaker = null; // for the fast green-border signal
    let silenceCounter = 0;
    const SILENCE_THRESHOLD = 3; // Number of silent intervals before clearing active speaker

    // Sustained speaker detection using sliding window majority vote.
    // volume-indicator fires every ~200ms. Used by Speaker Top, Focus, and
    // Speaker Side layouts to decide when to reposition the focused tile.
    // The Gallery layout uses its own size-based gating (>20 participants on
    // desktop) and ignores this threshold for stability.
    //
    // 40 events × 200ms ≈ 8 seconds — matches the spec from product:
    // "reposition the speaker after 8 seconds without checking number of users".
    let consecutiveCount = 0;
    let currentCandidate = null;
    let interruptionCount = 0;
    const SUSTAINED_THRESHOLD = 40; // 40 events × ~200ms ≈ 8 seconds
    const MAX_INTERRUPTION = 3;     // Tolerate ~600ms of interruption
    let lastSustainedCandidate = null;

    // Compute local user's numeric UID so we can remap volume-indicator UIDs
    const localNumericUid = participantId ? stringToNumericUid(participantId) : null;

    let lastVolumeTime = 0;
    let volumeEventCount = 0;

    // Debounce the active-speaker highlight so the green glow doesn't flicker
    // every 200ms as the loudest speaker bounces between participants. Require
    // the same speaker to win N consecutive events (~600ms) before switching
    // the highlight. This also hides client-to-client audio level jitter that
    // would otherwise make different users see different highlighted speakers.
    const ACTIVE_SPEAKER_MIN_EVENTS = 3;
    // Minimum volume level to count as "speaking". Agora's 0–100 scale:
    // typical background noise reads 10–25, quiet speech 40–60, normal
    // speech 60–90. Floor at 40 catches real voices while filtering out
    // breathing, HVAC, and keyboard clicks. 55 was too aggressive — quieter
    // mics never crossed it and the green indicator stopped appearing.
    const ACTIVE_SPEAKER_MIN_LEVEL = 40;
    let activeCandidate = null;
    let activeCandidateCount = 0;

    const handleVolumeIndicator = (volumes) => {
      const now = Date.now();
      const gap = lastVolumeTime ? now - lastVolumeTime : 0;
      lastVolumeTime = now;
      volumeEventCount++;

      // Log every event with all state so you can filter by [SPEAKER-DEBUG]
      let loudestSpeaker = null;
      let maxLevel = ACTIVE_SPEAKER_MIN_LEVEL;

      volumes.forEach((volume) => {
        if (volume.level > maxLevel) {
          maxLevel = volume.level;
          loudestSpeaker = volume.uid;
        }
      });

      // Remap local user's UID to string participantId.
      // Agora reports the local user as either localNumericUid OR uid=0
      // depending on SDK build/config — handle both. Without the uid=0
      // branch, activeSpeakerId gets stuck as "0" for the local user, which
      // never matches participantId in downstream consumers (adaptive encoder
      // 720p upgrade, optimizationMode 'detail' switch, green speaker border).
      if (loudestSpeaker !== null &&
          (loudestSpeaker === 0 || (localNumericUid && loudestSpeaker === localNumericUid))) {
        loudestSpeaker = participantId;
      }

      console.log(`[SPEAKER-DEBUG] event#${volumeEventCount} gap=${gap}ms loudest=${loudestSpeaker} level=${maxLevel} candidate=${currentCandidate} count=${consecutiveCount}/${SUSTAINED_THRESHOLD} sustained=${lastSustainedCandidate}`);

      if (loudestSpeaker) {
        silenceCounter = 0;

        const loudestStr = String(loudestSpeaker);

        // Instant signal (no debounce) — drives the green speaker border.
        // Updates on every event when the loudest speaker changes, so the
        // border appears ~200ms after someone starts speaking.
        if (loudestStr !== lastInstantSpeaker) {
          setInstantSpeakerId(loudestStr);
          lastInstantSpeaker = loudestStr;
        }

        // Debounce active-speaker highlight: only switch after N consecutive
        // events from the same speaker. Prevents per-frame flicker. Drives
        // the focus-tile swap in speaker layouts.
        if (loudestStr === activeCandidate) {
          activeCandidateCount++;
        } else {
          activeCandidate = loudestStr;
          activeCandidateCount = 1;
        }
        if (activeCandidateCount >= ACTIVE_SPEAKER_MIN_EVENTS && loudestStr !== lastActiveSpeaker) {
          setActiveSpeakerId(loudestStr);
          lastActiveSpeaker = loudestStr;
        }

        // Consecutive speaker tracking with interruption tolerance
        const speakerStr = String(loudestSpeaker);
        if (speakerStr === currentCandidate) {
          consecutiveCount++;
          interruptionCount = 0;
        } else if (currentCandidate && interruptionCount < MAX_INTERRUPTION) {
          interruptionCount++;
          consecutiveCount++;
        } else {
          currentCandidate = speakerStr;
          consecutiveCount = 1;
          interruptionCount = 0;
        }

        if (consecutiveCount >= SUSTAINED_THRESHOLD && currentCandidate !== lastSustainedCandidate) {
          lastSustainedCandidate = currentCandidate;
          setSustainedSpeakerId(currentCandidate);
          console.log(`[SPEAKER-DEBUG] >>> SWITCH! New sustained speaker: ${currentCandidate} after ${consecutiveCount} events at ${new Date().toISOString()}`);
        }
      } else {
        // No one speaking above threshold
        silenceCounter++;
        if (silenceCounter >= SILENCE_THRESHOLD) {
          lastActiveSpeaker = null;
          setActiveSpeakerId(null);
          lastInstantSpeaker = null;
          setInstantSpeakerId(null);
        }
        if (currentCandidate) {
          interruptionCount++;
          if (interruptionCount > MAX_INTERRUPTION) {
            currentCandidate = null;
            consecutiveCount = 0;
            interruptionCount = 0;
          }
        }
        // Don't clear sustainedSpeakerId — keep last sustained speaker in focus
      }
    };

    // Network quality event — Agora fires this every 2s with per-user stats
    const handleNetworkQuality = (stats) => {
      // stats: { uplinkNetworkQuality, downlinkNetworkQuality } for local user
      // For remote users, use client.getRemoteNetworkQuality()
      setNetworkQualities(() => {
        const next = new Map();
        // Local user quality
        next.set('local', { uplink: stats.uplinkNetworkQuality, downlink: stats.downlinkNetworkQuality });
        // Remote user qualities
        try {
          const remoteQualities = client.getRemoteNetworkQuality?.();
          if (remoteQualities) {
            Object.entries(remoteQualities).forEach(([uid, q]) => {
              next.set(Number(uid) || uid, { uplink: q.uplinkNetworkQuality, downlink: q.downlinkNetworkQuality });
            });
          }
        } catch (e) { /* ignore */ }
        return next;
      });
    };

    client.on('user-published', handleUserPublished);
    client.on('user-unpublished', handleUserUnpublished);
    client.on('user-joined', handleUserJoined);
    client.on('user-left', handleUserLeft);
    client.on('connection-state-change', handleConnectionStateChange);
    client.on('volume-indicator', handleVolumeIndicator);
    client.on('network-quality', handleNetworkQuality);

    // Monitor screenClient connection — if it disconnects while sharing,
    // clean up screen share state so host isn't stuck thinking they're still sharing.
    // Only clean up on DISCONNECTED (not RECONNECTING — Agora auto-reconnects).
    let screenReconnectTimer = null;
    let screenReconnectAttempts = 0;
    const handleScreenClientConnectionChange = (curState, prevState) => {
      console.log(`[AgoraContext] screenClient connection: ${prevState} -> ${curState}`);
      if (curState === 'RECONNECTING') {
        // Give Agora generous time to reconnect (60s) — network blips are common
        clearTimeout(screenReconnectTimer);
        screenReconnectAttempts++;
        console.log(`[AgoraContext] screenClient reconnecting (attempt ${screenReconnectAttempts})`);
        screenReconnectTimer = setTimeout(() => {
          if (isScreenSharingRef.current) {
            console.warn('[AgoraContext] screenClient reconnect timeout after 60s — cleaning up screen share');
            disableScreenShareRef.current?.();
          }
        }, 60000);
      } else if (curState === 'CONNECTED') {
        clearTimeout(screenReconnectTimer);
        if (screenReconnectAttempts > 0) {
          console.log(`[AgoraContext] screenClient reconnected successfully after ${screenReconnectAttempts} attempt(s)`);
          screenReconnectAttempts = 0;
        }
      } else if (curState === 'DISCONNECTED' && prevState !== 'DISCONNECTING') {
        // Brief network blips can go CONNECTED → DISCONNECTED without RECONNECTING.
        // Give a generous grace period (45s) — Agora often auto-recovers.
        clearTimeout(screenReconnectTimer);
        screenReconnectTimer = setTimeout(() => {
          if (isScreenSharingRef.current) {
            // Verify the track is actually dead before killing screen share
            const track = localScreenTrackRef?.current;
            const trackAlive = track && track.getMediaStreamTrack?.()?.readyState === 'live';
            if (trackAlive) {
              // Track is still live — Agora client disconnected but capture is fine.
              // Give another 30s grace period for Agora to auto-recover.
              console.log('[AgoraContext] screenClient disconnected but track still alive — extending grace period');
              screenReconnectTimer = setTimeout(() => {
                if (isScreenSharingRef.current) {
                  console.warn('[AgoraContext] screenClient still disconnected after extended grace — cleaning up');
                  disableScreenShareRef.current?.();
                }
              }, 30000);
            } else {
              console.warn('[AgoraContext] screenClient disconnect timeout after 45s, track dead — cleaning up screen share');
              disableScreenShareRef.current?.();
            }
          }
        }, 45000);
      } else if (curState === 'DISCONNECTED') {
        clearTimeout(screenReconnectTimer);
        console.warn('[AgoraContext] screenClient intentionally disconnected — cleaning up screen share');
        if (isScreenSharingRef.current) {
          disableScreenShareRef.current?.();
        }
      }
    };
    screenClient.on('connection-state-change', handleScreenClientConnectionChange);

    return () => {
      client.off('user-published', handleUserPublished);
      client.off('user-unpublished', handleUserUnpublished);
      client.off('user-joined', handleUserJoined);
      client.off('user-left', handleUserLeft);
      client.off('connection-state-change', handleConnectionStateChange);
      client.off('volume-indicator', handleVolumeIndicator);
      client.off('network-quality', handleNetworkQuality);
      screenClient.off('connection-state-change', handleScreenClientConnectionChange);
      clearTimeout(screenReconnectTimer);
    };
  }, [client, screenClient]);

  // Auto-join if configured
  useEffect(() => {
    console.log('[AgoraMeetingProvider] Auto-join effect check:', { joinWithoutUserInteraction, meetingId, joined: joinedRef.current });
    if (joinWithoutUserInteraction && meetingId && !joinedRef.current) {
      console.log('[AgoraMeetingProvider] Auto-join triggered');
      join();
    }
  }, [joinWithoutUserInteraction, meetingId, join]);

  // Sync participant info and screen share status via backend API
  useEffect(() => {
    let pollInterval = null;

    const fetchParticipantInfo = async () => {
      if (!meetingId) return;

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/agora/participant-info/${meetingId}`
        );
        const { participants: participantsData } = response.data;

        if (participantsData) {
          setRemoteUserMetadata(prev => {
            const newMap = new Map(prev);
            // Get our numeric UID to filter out our own info
            const ourNumericUid = String(stringToNumericUid(participantId));
            const newEntries = Object.entries(participantsData).filter(
              ([id, info]) => {
                if (id === participantId || id === 'local' || id === ourNumericUid) return false;
                // Filter out recorder bot by UID or name (always)
                // Worker-recorder browsers use UIDs in 999999990-999999999 range.
                const uid = parseInt(id);
                if (uid >= 999999990 && uid <= 999999999) return false;
                // STT bots filtered only when show-bot-in-participants toggle is off
                if (uid >= 999000 && uid <= 999999 && !showSttBotRef.current) return false;
                const name = info?.displayName || '';
                if (name.includes('Recorder') || name.includes('recorder')) return false;
                return true;
              }
            );
            // Stale-while-revalidate: if server returned fewer participants than we have,
            // it likely restarted and lost in-memory state. Keep existing data and merge new.
            // Each participant will re-register on their next poll cycle.
            newEntries.forEach(([id, info]) => {
              newMap.set(id, info);
            });
            return newMap;
          });
        }
      } catch (error) {
        // Silently ignore — keep existing cached data, next poll will retry
      }
    };

    // Store in ref so it can be called from user-joined handler
    fetchParticipantInfoRef.current = fetchParticipantInfo;

    const fetchScreenShareStatus = async () => {
      if (!meetingId) return;

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/agora/screen-share/${meetingId}`
        );
        const { isSharing, presenterId: remotePresenterId } = response.data;

        // Backend stores screen share UID, convert to main UID
        // Screen share UID = main UID + 1, so main UID = screen share UID - 1
        const mainPresenterId = remotePresenterId ? getMainUidFromScreenShare(Number(remotePresenterId)) : null;

        // Compare with our numeric UID
        const ourNumericUid = stringToNumericUid(participantId);
        const ourScreenShareUid = getScreenShareUid(ourNumericUid);

        // Update presenter if it's a remote user sharing (not us)
        // Check both the screen share UID and main UID against ours
        const isOurScreenShare = remotePresenterId &&
          (String(remotePresenterId) === String(ourScreenShareUid) || String(mainPresenterId) === String(ourNumericUid));

        if (isSharing && remotePresenterId && !isOurScreenShare) {
          const screenShareUid = Number(remotePresenterId);

          // Pre-register the screen share UID so future user-published events classify it correctly
          screenShareUidsRef.current.add(screenShareUid);

          // RECLASSIFY: If screen share UID is stuck in remoteUsers (race condition),
          // move it to remoteScreenShares so the presenter view renders correctly
          if (remoteUsersRef.current?.has(screenShareUid)) {
            const misclassified = remoteUsersRef.current.get(screenShareUid);
            console.log('[AgoraContext] Reclassifying screen share UID from remoteUsers:', screenShareUid);
            setRemoteScreenShares(prev => {
              const newMap = new Map(prev);
              newMap.set(screenShareUid, {
                uid: screenShareUid,
                mainUid: mainPresenterId,
                videoTrack: misclassified.videoTrack,
                audioTrack: misclassified.audioTrack,
              });
              return newMap;
            });
            setRemoteUsers(prev => {
              const newMap = new Map(prev);
              newMap.delete(screenShareUid);
              return newMap;
            });
          } else if (!remoteScreenSharesRef.current?.has(screenShareUid)) {
            // Screen share UID not in either map — try to subscribe from Agora client directly
            // This handles mobile where polling detects the presenter before user-published event fires
            const agoraUser = clientRef.current?.remoteUsers?.find(u => u.uid === screenShareUid);
            if (agoraUser) {
              console.log('[AgoraContext] Polling: Manually subscribing to screen share from Agora client:', screenShareUid);
              try {
                if (agoraUser.hasVideo && !agoraUser.videoTrack) {
                  await clientRef.current.subscribe(agoraUser, 'video');
                }
                if (agoraUser.hasAudio && !agoraUser.audioTrack) {
                  await clientRef.current.subscribe(agoraUser, 'audio');
                }
                setRemoteScreenShares(prev => {
                  const newMap = new Map(prev);
                  newMap.set(screenShareUid, {
                    uid: screenShareUid,
                    mainUid: mainPresenterId,
                    videoTrack: agoraUser.videoTrack || null,
                    audioTrack: agoraUser.audioTrack || null,
                  });
                  return newMap;
                });
              } catch (subErr) {
                console.warn('[AgoraContext] Failed to manually subscribe screen share:', subErr);
              }
            }
          }

          // Only update presenterId if it actually changed (avoids re-render flicker)
          // Normalize to string to prevent type mismatch (number vs string) causing spurious updates
          const normalizedPresenterId = String(mainPresenterId);
          if (String(presenterIdRef.current) !== normalizedPresenterId) {
            setPresenterId(normalizedPresenterId);
            callbacksRef.current.onPresenterChanged?.(normalizedPresenterId);
          }
        } else if (!isSharing && isScreenSharingRef.current) {
          // Backend lost our screen share state (e.g. PM2 restart) — re-register
          console.log('[AgoraContext] Backend lost screen share state, re-registering...');
          try {
            const ourNumUid = stringToNumericUid(participantId);
            const ourSSUid = getScreenShareUid(ourNumUid);
            await axios.post(`${process.env.REACT_APP_BASE_URL}/agora/screen-share/start`, {
              meetingId,
              participantId: String(ourSSUid),
              mainParticipantId: String(ourNumUid),
            });
            console.log('[AgoraContext] Screen share re-registered with backend');
          } catch (reregErr) {
            console.warn('[AgoraContext] Failed to re-register screen share:', reregErr);
          }
        } else if (!isSharing && !isScreenSharingRef.current) {
          // Backend says no one is sharing and we're not sharing locally.
          // But if we still have a remote screen share track subscribed,
          // the backend likely just restarted — keep showing the share.
          const hasRemoteScreenTrack = remoteScreenSharesRef.current?.size > 0;
          if (hasRemoteScreenTrack) {
            // Backend lost state but we still have the track — don't clear presenter
            // The sharer will re-register on their next poll cycle
          } else {
            setPresenterId(null);
          }
        }
      } catch (error) {
        // Silently ignore — transient network errors during backend restart
      }
    };

    const registerParticipantInfo = async () => {
      if (!meetingId || !participantId) return;

      try {
        // Use the actual Agora UID (may be auto-assigned if uid=0 fallback was used)
        const registrationUid = actualUidRef.current || stringToNumericUid(participantId);
        await axios.post(
          `${process.env.REACT_APP_BASE_URL}/agora/participant-info`,
          {
            meetingId,
            participantId: String(registrationUid),
            displayName: participantName,
            metaData: metaData,
          }
        );
        // Fetch all participants after registering
        await fetchParticipantInfo();
      } catch (error) {
        // Silently ignore — will retry on next poll cycle
      }
    };

    // Re-register our info periodically (every 10s) to survive backend restarts
    let reRegisterCount = 0;
    const pollAll = async () => {
      reRegisterCount++;
      // Re-register every 5th poll cycle (~10s) to keep our name in backend
      if (reRegisterCount % 5 === 0) {
        registerParticipantInfo();
      }
      await Promise.all([
        fetchParticipantInfo(),
        fetchScreenShareStatus(),
      ]);
    };

    // Register our info and start polling when connected
    if (meetingState === AgoraConstants.meetingState.CONNECTED && meetingId && participantId) {
      registerParticipantInfo();

      // Fetch participant info immediately after registering
      setTimeout(() => fetchParticipantInfo(), 500);

      // Immediately fetch screen share status so late joiners discover ongoing
      // screen shares without waiting for the first poll tick (2-5s).
      // This ensures screenShareUidsRef is populated before user-published events arrive,
      // and reclassifies any screen share UIDs already stuck in remoteUsers.
      fetchScreenShareStatus().catch(() => { /* ignore */ });

      // Poll for participant info and screen share status
      // Mobile uses slightly longer interval to reduce HTTP traffic that competes with WebRTC
      const pollMs = isMobileDevice() ? 5000 : 2000;
      pollInterval = setInterval(pollAll, pollMs);
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [meetingState, meetingId, participantId, participantName, metaData]);

  // Build participants map (VideoSDK-compatible format)
  const participants = useMemo(() => {
    const allParticipants = new Map();

    // Helper to check if participant is pinned (handles both string and numeric IDs)
    const isPinned = (id) => {
      return pinnedParticipants.has(id) ||
        pinnedParticipants.has(String(id)) ||
        (typeof id === 'string' && !isNaN(id) && pinnedParticipants.has(Number(id)));
    };

    // Add local participant (use meetingState instead of ref for reactivity)
    // Skip for recorder bot — it shouldn't appear in the participant grid
    const isRecorderBot = new URLSearchParams(window.location.search).get('recorder') === 'true';
    const isJoined = meetingState === AgoraConstants.meetingState.CONNECTED || meetingState === AgoraConstants.meetingState.RECONNECTING;
    if (isJoined && !isRecorderBot) {
      // Extract proper MediaStreamTracks from local Agora tracks
      const localVideoMediaTrack = localVideoTrack?.getMediaStreamTrack?.() || null;
      const localAudioMediaTrack = localAudioTrack?.getMediaStreamTrack?.() || null;
      const localScreenMediaTrack = localScreenTrack?.getMediaStreamTrack?.() || null;

      const localParticipantData = {
        id: participantId || 'local',
        displayName: participantName || 'You',
        isLocal: true,
        mode: mode,
        webcamOn: isWebcamOn,
        micOn: isMicOn,
        screenShareOn: isScreenSharing,
        // Store both the Agora track (for play()) and MediaStreamTrack (for MediaStream)
        webcamStream: localVideoTrack ? {
          track: localVideoMediaTrack,
          agoraTrack: localVideoTrack,
        } : null,
        micStream: localAudioTrack ? {
          track: localAudioMediaTrack,
          agoraTrack: localAudioTrack,
        } : null,
        screenShareStream: localScreenTrack ? {
          track: localScreenMediaTrack,
          agoraTrack: localScreenTrack,
        } : null,
        metaData: metaData,
        pinState: isPinned(participantId || 'local'),
      };

      // Store with string participantId ONLY (don't duplicate with numeric UID)
      allParticipants.set(participantId || 'local', localParticipantData);
    }

    // Calculate local user's screen share UID to filter it out
    const localNumericUid = participantId ? stringToNumericUid(participantId) : null;
    const localScreenShareUid = localNumericUid ? getScreenShareUid(localNumericUid) : null;

    // Add remote participants
    remoteUsers.forEach((user, uid) => {
      // Skip if this is a screen share UID (screen share UIDs are handled separately)
      if (remoteScreenShares.has(uid)) {
        return;
      }

      // Always skip recorder bot UIDs; STT bots only when show-bot-in-participants toggle is off.
      // Worker-recorder browsers use UIDs in 999999990-999999999 range.
      if (uid >= 999999990 && uid <= 999999999) {
        return;
      }
      if ((uid >= 999000 && uid <= 999999) && !showSttBot) {
        return;
      }
      // Defense-in-depth: also filter by display name in case a recorder slips
      // past the UID range (e.g. worker uses an out-of-range UID, or a breakout
      // recorder whose UIDs live in a different reserved range). The recorder
      // browser self-sets its name to "R-Link Recorder" in JoiningScreenNew.js.
      const earlyUserMeta = remoteUserMetadata.get(uid) || remoteUserMetadata.get(String(uid));
      const earlyName = earlyUserMeta?.displayName || user.displayName || '';
      if (earlyName === 'R-Link Recorder' || earlyName === 'Recorder') {
        return;
      }

      // IMPORTANT: Skip the LOCAL user's screen share UID
      // When we share screen, our screen share client joins with UID = ourMainUid + 1
      // This can appear as a "remote user" but should not be shown as a participant
      if (localScreenShareUid && uid === localScreenShareUid) {
        console.log('Filtering out local screen share UID from participants:', uid);
        return;
      }

      // Additional check: Skip if this UID looks like a screen share UID
      // (i.e., if uid - 1 exists as another remote user, this is likely a screen share)
      const potentialMainUid = uid - 1;
      if (remoteUsers.has(potentialMainUid)) {
        console.log('Filtering out likely screen share UID from participants:', uid, 'main:', potentialMainUid);
        return;
      }

      // Skip if uid - 1 matches the local user's numeric UID (it's our own screen share)
      if (localNumericUid && potentialMainUid === localNumericUid) {
        console.log('Filtering out local screen share UID (matches local + 1):', uid);
        return;
      }

      // Also skip if we don't have metadata and the UID looks like it could be a screen share
      // (no display name metadata = likely a screen share client)
      const userMetadataCheck = remoteUserMetadata.get(uid) || remoteUserMetadata.get(String(uid));
      if (!userMetadataCheck && remoteUsers.size > 1) {
        // Check if any other user's UID + 1 equals this UID
        for (const [otherUid] of remoteUsers) {
          if (otherUid !== uid && otherUid + 1 === uid) {
            console.log('Filtering out screen share UID (no metadata, matches pattern):', uid);
            return;
          }
        }
      }

      // Extract proper MediaStreamTracks from Agora tracks
      const videoMediaTrack = user.videoTrack?.getMediaStreamTrack?.() || null;
      const audioMediaTrack = user.audioTrack?.getMediaStreamTrack?.() || null;

      // Get display name from metadata (try both numeric and string UID)
      const userMetadata = remoteUserMetadata.get(user.uid) || remoteUserMetadata.get(String(user.uid));
      const isSttBotUid = uid >= 999000 && uid <= 999999;
      const displayName = isSttBotUid
        ? 'AI Notetaker'
        : (userMetadata?.displayName || user.displayName || `Participant ${user.uid}`);

      // Check if this user has an associated screen share
      // Screen share UID is main UID + 1
      const screenShareUid = getScreenShareUid(uid);
      const screenShare = remoteScreenShares.get(screenShareUid);
      const hasScreenShare = !!screenShare;

      // Get screen share track data if available
      const screenShareVideoTrack = screenShare?.videoTrack;
      const screenShareMediaTrack = screenShareVideoTrack?.getMediaStreamTrack?.() || null;

      // For webinars, determine mode from role metadata — attendees are RECV_ONLY.
      // For regular meetings, all remote participants are SEND_AND_RECV.
      let remoteMode = AgoraConstants.modes.SEND_AND_RECV;
      if (meetingSettings?.roomType === 'Webinar') {
        const STAGE_ROLES = ['moderator', 'co-host', 'host', 'presenter', 'speaker'];
        const remoteRole = userMetadata?.metaData?.meetingRole;
        if (remoteRole && !STAGE_ROLES.includes(remoteRole)) {
          remoteMode = AgoraConstants.modes.RECV_ONLY;
        }
      }

      allParticipants.set(user.uid, {
        id: user.uid,
        displayName: displayName,
        isLocal: false,
        mode: remoteMode,
        webcamOn: !!user.videoTrack,
        micOn: !!user.audioTrack,
        screenShareOn: hasScreenShare,
        // Store both the Agora track (for play()) and MediaStreamTrack (for MediaStream)
        webcamStream: user.videoTrack ? {
          track: videoMediaTrack,
          agoraTrack: user.videoTrack,
        } : null,
        micStream: user.audioTrack ? {
          track: audioMediaTrack,
          agoraTrack: user.audioTrack,
        } : null,
        // Screen share comes from separate UID
        screenShareStream: hasScreenShare ? {
          track: screenShareMediaTrack,
          agoraTrack: screenShareVideoTrack,
        } : null,
        metaData: userMetadata?.metaData || {},
        pinState: isPinned(user.uid),
      });
    });

    // SCREEN-SHARE-ONLY participants:
    // For each remote screen share, ensure there's a participant entry for its main UID.
    // Without this, when a presenter shares screen with camera/mic off (or for the
    // recorder bot during the brief race window before the main user is subscribed),
    // useParticipant(presenterId) returns nothing and PresenterView renders nothing —
    // so the recorded video drops the screen share entirely.
    remoteScreenShares.forEach((screenShare, screenShareUid) => {
      const mainUid = typeof screenShareUid === 'number'
        ? screenShareUid - 1
        : Number(screenShareUid) - 1;
      if (isNaN(mainUid)) return;
      // Skip if our local screen share
      if (localNumericUid != null && mainUid === localNumericUid) return;
      // Skip if the main user already has an entry
      if (allParticipants.has(mainUid) || allParticipants.has(String(mainUid))) return;

      const ssVideoTrack = screenShare?.videoTrack || null;
      const ssAudioTrack = screenShare?.audioTrack || null;
      const ssVideoMediaTrack = ssVideoTrack?.getMediaStreamTrack?.() || null;

      // Try to get a display name from metadata
      const meta = remoteUserMetadata.get(mainUid) || remoteUserMetadata.get(String(mainUid));
      const displayName = meta?.displayName || `Presenter`;

      allParticipants.set(mainUid, {
        id: mainUid,
        displayName,
        isLocal: false,
        mode: AgoraConstants.modes.SEND_AND_RECV,
        webcamOn: false,
        micOn: false,
        screenShareOn: true,
        webcamStream: null,
        micStream: null,
        screenShareStream: {
          track: ssVideoMediaTrack,
          agoraTrack: ssVideoTrack,
        },
        screenShareAudioStream: ssAudioTrack
          ? { track: ssAudioTrack.getMediaStreamTrack?.() || null, agoraTrack: ssAudioTrack }
          : null,
        metaData: meta?.metaData || {},
        pinState: isPinned(mainUid),
      });
    });

    return allParticipants;
  }, [meetingState, remoteUsers, remoteUserMetadata, remoteScreenShares, participantId, participantName, isWebcamOn, isMicOn, isScreenSharing, localVideoTrack, localAudioTrack, localScreenTrack, mode, metaData, pinnedParticipants, presenterId, meetingSettings, showSttBot]);

  const localParticipant = participants.get(participantId || 'local');

  // Reactive local encoder configuration based on participant count + sustained speaker
  // Desktop only: adapts resolution based on participant count and active speaker status.
  // DISABLED on mobile: all mobile profiles are identical, so encoder reconfiguration
  // is unnecessary and can disrupt the audio codec pipeline causing audio cutouts.
  const lastVideoProfileRef = useRef(null);
  const downgradeTimerRef = useRef(null);
  // Target of the currently-pending downgrade (if any). Used to avoid resetting
  // the 3s timer when activeSpeakerId rotates between OTHER (non-local) speakers
  // — without this, the local user's pending downgrade gets cancelled every
  // time someone else takes the floor and never actually fires.
  const pendingDowngradeTargetRef = useRef(null);

  // Mount-only cleanup: clear any pending downgrade timer on unmount so the
  // setTimeout callback doesn't fire after the provider tears down. We do NOT
  // clear inside the main effect's React cleanup — that runs on every dep
  // change and would defeat the dedupe logic below.
  useEffect(() => {
    return () => {
      if (downgradeTimerRef.current) {
        clearTimeout(downgradeTimerRef.current);
        downgradeTimerRef.current = null;
        pendingDowngradeTargetRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!localVideoTrack || !isWebcamOn) return;

    // Skip adaptive encoding entirely on mobile — mobile profiles are all identical
    // and calling setEncoderConfiguration disrupts the audio pipeline on mobile browsers
    if (isMobileDevice()) {
      return;
    }

    const totalParticipants = remoteUsers.size + 1; // +1 for local user
    const baseProfile = getAdaptiveVideoProfile(totalParticipants);

    // Promote on activeSpeakerId (~600ms debounce) rather than sustainedSpeakerId
    // (8s) — real conversation rarely keeps one dominant speaker for 8s, so the
    // 8s gate caused the HD upgrade to almost never fire. The asymmetric
    // upgrade-now / 3s-downgrade-delay below prevents encoder thrashing on
    // rapid speaker swaps.
    //
    // The volume-indicator handler tries to remap the local user's numeric UID
    // back to participantId, but that remap can miss (Agora reports uid=0 for
    // local in some configs), so activeSpeakerId may be either the string
    // participantId OR the hashed numeric UID. Check both — same pattern as
    // isActiveSpeaker() in useAgoraParticipant.
    const isLocalActiveSpeaker = (() => {
      if (!activeSpeakerId || !participantId) return false;
      const a = String(activeSpeakerId);
      if (a === String(participantId)) return true;
      if (a === String(stringToNumericUid(participantId))) return true;
      return false;
    })();
    const profiles = getVideoProfiles();

    // Webinar stage roles broadcast continuously to attendees, so pin
    // them to the standard HIGH (720p) encoder regardless of audience
    // size — without this, getAdaptiveVideoProfile drops them to 540p
    // at 5+ participants and they look pixelated.
    //
    // NOTE: previously tried a dedicated 1080p `webinarStage` profile
    // here. Reverted on 2026-04-28 after a live host saw their video go
    // completely black after hard-refresh — likely cameras/bandwidth that
    // couldn't sustain a hot setEncoderConfiguration to 1080p mid-session.
    // Sticking to 720p (the SDK-default high) is safer until we add a
    // capability probe.
    const isWebinar = meetingSettings?.roomType === 'Webinar';
    const STAGE_ROLES = ['moderator', 'host', 'co-host', 'presenter', 'speaker'];
    const isLocalStageRole = STAGE_ROLES.includes(metaData?.meetingRole);
    const forceWebinarHigh = isWebinar && isLocalStageRole;

    // C2 — when recording is active, pin stage roles' publish profile to
    // 720p with a HIGH bitrate floor so Agora ABR cannot silently drop
    // them to 360p mid-recording (the soft/blocky-host symptom). We do
    // NOT bump resolution to 1080p here — the 2026-04-28 attempt at a
    // hot 1080p switch made some hosts go black after refresh (see
    // VIDEO_PROFILES.webinarStage comment). Floor at 1500 kbps min /
    // 2500 kbps target instead — same effect on perceived quality without
    // the black-screen risk. Restored automatically when isRecording flips
    // back to false because this useEffect re-runs and recordingPinned
    // becomes null.
    const forceRecordingHigh = isRecording && isLocalStageRole && !isMobileDevice();
    const recordingPinned = forceRecordingHigh
      ? {
          width: profiles.high.width,
          height: profiles.high.height,
          frameRate: 30,
          bitrateMin: 1500,
          bitrateMax: 2500,
        }
      : null;

    const targetProfile = recordingPinned
      || ((isLocalActiveSpeaker || forceWebinarHigh) ? profiles.high : baseProfile);

    // Deep-compare profile values to avoid unnecessary encoder calls.
    // Include bitrateMin so the recording-pinned profile is recognized as
    // distinct from the otherwise-identical 720p adaptive profile.
    const last = lastVideoProfileRef.current;
    if (last &&
      last.width === targetProfile.width &&
      last.height === targetProfile.height &&
      last.frameRate === targetProfile.frameRate &&
      last.bitrateMax === targetProfile.bitrateMax &&
      last.bitrateMin === targetProfile.bitrateMin) {
      return;
    }

    const applyProfile = (profile) => {
      lastVideoProfileRef.current = profile;
      setCurrentVideoProfile(profile);
      try {
        localVideoTrack.setEncoderConfiguration(profile);
        console.log(
          `[AgoraMeetingProvider] Adaptive encoder: ${profile.width}x${profile.height} (${totalParticipants} participants, activeSpeaker=${isLocalActiveSpeaker})`
        );
      } catch (err) {
        console.warn('[AgoraMeetingProvider] Failed to set encoder config:', err);
      }
    };

    const profilesEqual = (a, b) => !!a && !!b &&
      a.width === b.width &&
      a.height === b.height &&
      a.frameRate === b.frameRate &&
      a.bitrateMax === b.bitrateMax &&
      a.bitrateMin === b.bitrateMin;

    if (targetProfile === profiles.high || targetProfile === recordingPinned) {
      // Upgrade (or pinned-high): cancel any pending downgrade and apply now.
      if (downgradeTimerRef.current) {
        clearTimeout(downgradeTimerRef.current);
        downgradeTimerRef.current = null;
        pendingDowngradeTargetRef.current = null;
      }
      applyProfile(targetProfile);
    } else {
      // Downgrade. If a downgrade to the same target is already pending,
      // leave the existing 3s timer alone — don't reset it just because
      // activeSpeakerId rotated to a different (still non-local) speaker.
      if (downgradeTimerRef.current && profilesEqual(pendingDowngradeTargetRef.current, targetProfile)) {
        return;
      }
      if (downgradeTimerRef.current) {
        clearTimeout(downgradeTimerRef.current);
        downgradeTimerRef.current = null;
      }
      pendingDowngradeTargetRef.current = targetProfile;
      downgradeTimerRef.current = setTimeout(() => {
        downgradeTimerRef.current = null;
        pendingDowngradeTargetRef.current = null;
        applyProfile(targetProfile);
      }, 3000);
    }
  }, [remoteUsers.size, localVideoTrack, isWebcamOn, activeSpeakerId, participantId, meetingSettings?.roomType, metaData?.meetingRole, isRecording]);

  // Active-speaker optimizationMode switching: 'detail' prioritizes resolution
  // clarity (sharp face for the recording), 'motion' prioritizes smoothness
  // for everyone else. Driven by activeSpeakerId (~600ms debounce) — same
  // signal as the green speaker border, so what users see matches what gets
  // recorded crisp. Skipped on mobile (matches the encoder-reconfig pattern
  // above; setOptimizationMode is also Chrome-only per Agora docs).
  const lastOptimizationModeRef = useRef(null);
  useEffect(() => {
    if (!localVideoTrack || !isWebcamOn) return;
    if (isMobileDevice()) return;
    if (typeof localVideoTrack.setOptimizationMode !== 'function') return;

    // Match either string participantId or hashed numeric UID — see comment
    // above the adaptive-encoder useEffect for why both forms appear.
    const isLocalActiveSpeaker = (() => {
      if (!activeSpeakerId || !participantId) return false;
      const a = String(activeSpeakerId);
      if (a === String(participantId)) return true;
      if (a === String(stringToNumericUid(participantId))) return true;
      return false;
    })();
    const targetMode = isLocalActiveSpeaker ? 'detail' : 'motion';

    if (lastOptimizationModeRef.current === targetMode) return;
    lastOptimizationModeRef.current = targetMode;

    localVideoTrack.setOptimizationMode(targetMode)
      .then(() => {
        setCurrentOptimizationMode(targetMode);
        console.log(`[AgoraMeetingProvider] optimizationMode → ${targetMode} (activeSpeaker=${isLocalActiveSpeaker})`);
      })
      .catch((err) => console.warn('[AgoraMeetingProvider] setOptimizationMode failed:', err));
  }, [activeSpeakerId, participantId, localVideoTrack, isWebcamOn]);

  // Pin/unpin participants - handle both string and numeric IDs
  const pin = useCallback((participantIdToPin) => {
    console.log('[AgoraContext] pin called with:', participantIdToPin, 'type:', typeof participantIdToPin);
    setPinnedParticipants(prev => {
      const newMap = new Map(prev);
      // Store with both formats to ensure matching works
      newMap.set(participantIdToPin, { cam: true, share: false });
      // Also store as string if numeric
      if (typeof participantIdToPin === 'number') {
        newMap.set(String(participantIdToPin), { cam: true, share: false });
      }
      // Also store as number if string
      if (typeof participantIdToPin === 'string' && !isNaN(participantIdToPin)) {
        newMap.set(Number(participantIdToPin), { cam: true, share: false });
      }
      console.log('[AgoraContext] pinnedParticipants after pin:', [...newMap.keys()]);
      return newMap;
    });
  }, []);

  const unpin = useCallback((participantIdToUnpin) => {
    console.log('[AgoraContext] unpin called with:', participantIdToUnpin, 'type:', typeof participantIdToUnpin);
    setPinnedParticipants(prev => {
      const newMap = new Map(prev);
      // Remove both formats
      newMap.delete(participantIdToUnpin);
      if (typeof participantIdToUnpin === 'number') {
        newMap.delete(String(participantIdToUnpin));
      }
      if (typeof participantIdToUnpin === 'string' && !isNaN(participantIdToUnpin)) {
        newMap.delete(Number(participantIdToUnpin));
      }
      console.log('[AgoraContext] pinnedParticipants after unpin:', [...newMap.keys()]);
      return newMap;
    });
  }, []);

  // Noise cancellation control functions
  const toggleNoiseCancellation = useCallback(async (enable, mode, level) => {
    console.log('[AgoraContext] toggleNoiseCancellation called, enable:', enable, 'mode:', mode, 'level:', level);
    setNoiseCancellationEnabled(enable);

    const effectiveMode = mode || noiseCancellationModeRef.current;
    const effectiveLevel = level || noiseCancellationLevelRef.current;

    if (mode) {
      setNoiseCancellationModeState(mode);
      noiseCancellationModeRef.current = mode;
    }
    if (level) {
      setNoiseCancellationLevelState(level);
      noiseCancellationLevelRef.current = level;
    }

    if (localAudioTrack) {
      try {
        if (enable) {
          const success = await enableNoiseCancellation(localAudioTrack, effectiveMode, effectiveLevel);
          console.log('[AgoraContext] Noise cancellation toggled on:', success);
        } else {
          await disableNoiseCancellation(localAudioTrack);
          console.log('[AgoraContext] Noise cancellation toggled off');
        }
      } catch (error) {
        console.error('[AgoraContext] Failed to toggle noise cancellation:', error);
      }
    }
  }, [localAudioTrack]);

  const changeNoiseCancellationMode = useCallback(async (mode) => {
    console.log('[AgoraContext] changeNoiseCancellationMode called, mode:', mode);
    setNoiseCancellationModeState(mode);

    // Determine the appropriate level for this mode from the presets
    const preset = mode === 'NSNG'
      ? NOISE_CANCELLATION_LEVELS.HIGH
      : NOISE_CANCELLATION_LEVELS.LOW;
    const level = preset.level || 'SOFT';
    setNoiseCancellationLevelState(level);

    if (localAudioTrack && noiseCancellationEnabledRef.current) {
      try {
        await setNoiseCancellationMode(mode);
        await setNoiseCancellationLevel(level);
        console.log('[AgoraContext] Noise cancellation mode changed to:', mode, 'level:', level);
      } catch (error) {
        console.error('[AgoraContext] Failed to change noise cancellation mode:', error);
      }
    }
  }, [localAudioTrack]);

  // Track all participants throughout the entire meeting (cumulative, with roles)
  useEffect(() => {
    participants.forEach(p => {
      const name = p.displayName || p.participant?.displayName;
      if (name) {
        const role = p.metaData?.meetingRole || 'attendee';
        const entry = { name, role };
        meetingParticipantsRef.current.set(name, entry);
        if (isRecording) recordingParticipantsRef.current.set(name, entry);
      }
    });
  }, [isRecording, participants]);

  // Agora Cloud Recording - start
  const startRecording = useCallback(async (recordingLayout) => {
    if (!meetingId) return;
    try {
      setRecordingState(AgoraConstants.recordingEvents.RECORDING_STARTING);
      recordingParticipantsRef.current = new Map(); // Reset for new recording
      // Generate sessionGroupId on first recording start (persists across start/stop cycles)
      if (!sessionGroupIdRef.current) {
        sessionGroupIdRef.current = `${meetingId}-${Date.now()}`;
      }
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/recording/start`,
        { meetingId, channelName: meetingId, sessionGroupId: sessionGroupIdRef.current, recordingLayout: recordingLayout || 'speaker-top' },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.success) {
        setRecordingState(AgoraConstants.recordingEvents.RECORDING_STARTED);
        setIsRecording(true);
        recordingStartedAtRef.current = Date.now();
        currentRecordingIdRef.current = response.data.sid || 'pending';
        recordingResourceIdRef.current = response.data.resourceId || null;
        recordingSidRef.current = response.data.sid || null;
        // Surface the Mongo recording _id so the hold-overlay can poll
        // /recording/:id/processing-status for the capture_started signal.
        setActiveRecordingId(response.data.recordingId || null);

        // Notify MeetingContainer so it can broadcast via Socket.IO
        callbacksRef.current.onRecordingStateChanged?.({ status: AgoraConstants.recordingEvents.RECORDING_STARTED });

        // Poll recording status every 10s to stay in sync
        recordingPollRef.current = setInterval(async () => {
          try {
            const statusRes = await axios.get(
              `${process.env.REACT_APP_BASE_URL}/recording/status/${meetingId}`
            );
            if (!statusRes.data.isRecording) {
              setRecordingState(AgoraConstants.recordingEvents.RECORDING_STOPPED);
              setIsRecording(false);
              setIsTranscribing(false);
              clearInterval(recordingPollRef.current);
              recordingPollRef.current = null;
            } else {
              setIsTranscribing(!!statusRes.data.isTranscribing);
            }
          } catch (err) {
            console.error('[AgoraContext] Error polling recording status:', err);
          }
        }, 10000);
      } else {
        setRecordingState(AgoraConstants.recordingEvents.RECORDING_STOPPED);
        console.error('[AgoraContext] Failed to start recording:', response.data.message);
      }
    } catch (error) {
      console.error('[AgoraContext] Error starting recording:', error);
      setRecordingState(AgoraConstants.recordingEvents.RECORDING_STOPPED);
      setIsRecording(false);
    }
  }, [meetingId]);

  // Agora Cloud Recording - stop
  const stopRecording = useCallback(async () => {
    if (!meetingId) return;
    try {
      setRecordingState(AgoraConstants.recordingEvents.RECORDING_STOPPING);

      // Collect participants — use cumulative map (captures everyone who was
      // in the meeting during recording, not just those still connected now)
      participants.forEach(p => {
        const name = p.displayName || p.participant?.displayName;
        if (name) {
          const role = p.metaData?.meetingRole || 'attendee';
          recordingParticipantsRef.current.set(name, { name, role });
        }
      });
      const participantNames = Array.from(recordingParticipantsRef.current.values());

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/recording/stop`,
        {
          meetingId,
          participants: participantNames,
          sessionGroupId: sessionGroupIdRef.current,
          resourceId: recordingResourceIdRef.current,
          sid: recordingSidRef.current,
          meetingName: customMeetingName || meetingSettings?.meetingName || meetingSettings?.roomName || '',
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.recording && response.data.recording.id) {
        currentRecordingIdRef.current = response.data.recording.id;
      }

      setRecordingState(AgoraConstants.recordingEvents.RECORDING_STOPPED);
      setIsRecording(false);
      setIsTranscribing(false);
      setActiveRecordingId(null);
      recordingStoppedAtRef.current = Date.now();

      // Notify MeetingContainer so it can broadcast via Socket.IO
      callbacksRef.current.onRecordingStateChanged?.({ status: AgoraConstants.recordingEvents.RECORDING_STOPPED });

      if (recordingPollRef.current) {
        clearInterval(recordingPollRef.current);
        recordingPollRef.current = null;
      }

      recordingStartedAtRef.current = null;
      recordingResourceIdRef.current = null;
      recordingSidRef.current = null;
    } catch (error) {
      console.error('[AgoraContext] Error stopping recording:', error);
      setRecordingState(AgoraConstants.recordingEvents.RECORDING_STOPPED);
      setIsRecording(false);
      setIsTranscribing(false);
      callbacksRef.current.onRecordingStateChanged?.({ status: AgoraConstants.recordingEvents.RECORDING_STOPPED });
    }
  }, [meetingId, participants]);

  // Pause / Resume (worker-recorder mode only)
  const [isRecordingPaused, setIsRecordingPaused] = useState(false);

  const pauseRecording = useCallback(async () => {
    if (!meetingId) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/recording/pause`,
        { meetingId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsRecordingPaused(true);
    } catch (error) {
      console.error('[AgoraContext] Error pausing recording:', error);
      throw error;
    }
  }, [meetingId]);

  const resumeRecording = useCallback(async () => {
    if (!meetingId) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/recording/resume`,
        { meetingId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsRecordingPaused(false);
    } catch (error) {
      console.error('[AgoraContext] Error resuming recording:', error);
      throw error;
    }
  }, [meetingId]);

  // Global recording status poll — keeps ALL participants in sync
  // Only runs when connected and recording poll isn't already active (the starter has their own poll)
  useEffect(() => {
    if (meetingState !== AgoraConstants.meetingState.CONNECTED || !meetingId) return;

    const globalPoll = setInterval(async () => {
      // Skip if this participant started the recording (they already have their own poll)
      if (recordingPollRef.current) return;
      try {
        const statusRes = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/recording/status/${meetingId}`
        );
        const serverIsRecording = statusRes.data.isRecording;
        // Guard: if we very recently flipped to stopped, ignore late-arriving
        // `true` responses that were in flight while the backend was still
        // tearing down the active recording. Prevents the poll from reviving
        // the indicator after the authoritative stop.
        if (serverIsRecording && Date.now() - recordingStoppedAtRef.current < 15000) {
          return;
        }
        setIsRecording(prev => {
          if (prev !== serverIsRecording) {
            setRecordingState(serverIsRecording
              ? AgoraConstants.recordingEvents.RECORDING_STARTED
              : AgoraConstants.recordingEvents.RECORDING_STOPPED);
            if (serverIsRecording) {
              callbacksRef.current.onRecordingStateChanged?.({ status: AgoraConstants.recordingEvents.RECORDING_STARTED });
            } else {
              callbacksRef.current.onRecordingStateChanged?.({ status: AgoraConstants.recordingEvents.RECORDING_STOPPED });
            }
          }
          return serverIsRecording;
        });
        setIsTranscribing(!!statusRes.data.isTranscribing);
      } catch (_) { /* ignore */ }
    }, 10000);

    return () => clearInterval(globalPoll);
  }, [meetingState, meetingId]);

  // Meeting object (VideoSDK-compatible interface)
  const mMeeting = useMemo(() => ({
    // State
    meetingId,
    localParticipant,
    participants,
    presenterId,
    activeSpeakerId,
    instantSpeakerId,
    sustainedSpeakerId,
    pinnedParticipants,
    isRecording,

    // Recording state
    recordingState,
    activeRecordingId,
    isTranscribing,

    // HLS state (stub - Agora doesn't have built-in HLS)
    hlsState: AgoraConstants.hlsEvents.HLS_STOPPED,
    hlsUrls: null,

    // Local state
    localMicOn: isMicOn,
    localWebcamOn: isWebcamOn,
    localScreenShareOn: isScreenSharing,

    // Noise cancellation state
    noiseCancellationEnabled,
    noiseCancellationMode,
    NOISE_CANCELLATION_LEVELS,

    // Check if there's actually an active screen share stream (local or remote)
    // Screen share is active if: we're sharing locally, OR remote screen share tracks exist,
    // OR polling detected a presenter (covers mobile where track may arrive after presenterId is set)
    hasActiveScreenShare: isScreenSharing || remoteScreenShares.size > 0 || !!presenterId,

    // Actions
    join,
    leave,
    end,
    muteMic,
    unmuteMic,
    toggleMic,
    silenceRemoteUser,
    restoreRemoteUserVolume,
    disableWebcam,
    enableWebcam,
    toggleWebcam,
    changeWebcam,
    enableScreenShare,
    disableScreenShare,
    toggleScreenShare,
    pin,
    unpin,

    // Recording (Agora Cloud Recording)
    startRecording, // eslint-disable-line no-undef
    stopRecording, // eslint-disable-line no-undef
    pauseRecording, // eslint-disable-line no-undef
    resumeRecording, // eslint-disable-line no-undef
    isRecordingPaused, // eslint-disable-line no-undef
    setRecordingState, // Exposed for MeetingStateContext socket sync
    setIsRecording, // Exposed for MeetingStateContext socket sync
    meetingParticipantsRef, // Cumulative set of all participant names during the meeting

    // Livestream (stubs - need Agora RTMP integration)
    startLivestream: () => {
      console.warn('Livestream requires Agora RTMP streaming setup');
      return Promise.resolve();
    },
    stopLivestream: () => {
      console.warn('Livestream requires Agora RTMP streaming setup');
      return Promise.resolve();
    },

    // Mode change for webinar promote/demote (audience ↔ host role in Agora)
    changeMode: async (newMode) => {
      try {
        if (newMode === AgoraConstants.modes.SEND_AND_RECV) {
          // Promote: audience → host (need publisher token first)
          const localUid = stringToNumericUid(participantId);
          const publisherToken = await getAgoraToken(meetingId, localUid, 'publisher');
          if (publisherToken) {
            await client.renewToken(publisherToken);
          }
          await client.setClientRole('host');
          console.log('[AgoraContext] Mode changed to SEND_AND_RECV (host)');
        } else {
          // Demote: host → audience (stop screen share + unpublish tracks first)
          // Must stop screen share before demoting — otherwise screenClient stays orphaned
          if (isScreenSharingRef.current) {
            console.log('[AgoraContext] Stopping screen share before demoting to audience');
            await disableScreenShareRef.current?.();
          }
          try {
            const tracksToUnpublish = [];
            if (localAudioTrackRef.current) tracksToUnpublish.push(localAudioTrackRef.current);
            if (localVideoTrackRef.current) tracksToUnpublish.push(localVideoTrackRef.current);
            if (tracksToUnpublish.length > 0) {
              await client.unpublish(tracksToUnpublish);
            }
          } catch (e) {
            console.warn('[AgoraContext] Error unpublishing before demote:', e);
          }
          await client.setClientRole('audience');
          setIsMicOn(false);
          setIsWebcamOn(false);
          console.log('[AgoraContext] Mode changed to RECV_ONLY (audience)');
        }
      } catch (error) {
        console.error('[AgoraContext] changeMode failed:', error);
      }
    },

    // Compatibility methods
    getWebcams: async () => {
      const devices = await AgoraRTC.getCameras();
      return devices;
    },
    getMics: async () => {
      const devices = await AgoraRTC.getMicrophones();
      return devices;
    },
    getSpeakers: async () => {
      const devices = await AgoraRTC.getPlaybackDevices();
      return devices;
    },
    changeMic: async (deviceId) => {
      if (localAudioTrack) {
        await localAudioTrack.setDevice(deviceId);
        // Track identity stays the same across setDevice(), so the denoiser
        // pipe should still be valid. Verify to catch any singleton drift
        // from a prior reconnect / toggle-while-muted sequence.
        if (noiseCancellationEnabledRef.current && !isMobileDevice()) {
          try {
            await verifyDenoiserAttached(
              localAudioTrack,
              noiseCancellationModeRef.current,
              noiseCancellationLevelRef.current
            );
          } catch (ncErr) {
            console.warn('[AgoraContext] verifyDenoiserAttached after changeMic failed:', ncErr);
          }
        }
      }
    },
    changeWebcamDevice: async (deviceId) => {
      // On mobile, custom tracks don't support setDevice — teardown and recreate
      if (isMobileDevice()) {
        if (_switchingCamera) return;
        _switchingCamera = true;
        try {
          if (localVideoTrack) {
            await client.unpublish(localVideoTrack);
            localVideoTrack.stop();
            localVideoTrack.close();
          }
          const totalParticipants = remoteUsers.size + 1;
          const videoTrack = await createMobilePortraitTrack(
            getAdaptiveVideoProfile(totalParticipants),
            deviceId
          );
          setLocalVideoTrack(videoTrack);
          await client.publish(videoTrack);
          setIsWebcamOn(true);
        } catch (err) {
          console.error('[AgoraContext] Mobile camera switch failed:', err);
        } finally {
          _switchingCamera = false;
        }
      } else if (localVideoTrack && localVideoTrack.setDevice) {
        await localVideoTrack.setDevice(deviceId);
      }
    },

    // Adaptive subscriptions (Agora handles this automatically, stub for VideoSDK compatibility)
    enableAdaptiveSubscriptions: () => {
      console.log('Agora handles adaptive subscriptions automatically');
    },

    // Noise cancellation controls
    toggleNoiseCancellation,
    changeNoiseCancellationMode,
    getNoiseCancellationStatus,

    // Current video encoder profile and label (e.g. "1280x720")
    currentVideoProfile,
    currentVideoProfileLabel: getVideoProfileLabel(currentVideoProfile),

    // Current encoder optimizationMode ('motion' | 'detail')
    currentOptimizationMode,
  }), [
    meetingId, localParticipant, participants, presenterId, activeSpeakerId, instantSpeakerId, sustainedSpeakerId,
    pinnedParticipants, isRecording, isMicOn, isWebcamOn, isScreenSharing, remoteScreenShares,
    join, leave, end, muteMic, unmuteMic, toggleMic, disableWebcam, enableWebcam,
    toggleWebcam, changeWebcam, enableScreenShare, disableScreenShare, toggleScreenShare,
    pin, unpin, localAudioTrack, localVideoTrack,
    noiseCancellationEnabled, noiseCancellationMode, toggleNoiseCancellation, changeNoiseCancellationMode,
    currentVideoProfile, currentOptimizationMode
  ]);

  // Function to set remote video stream type for simulcasting
  const setRemoteVideoStreamType = useCallback(async (uid, streamType) => {
    try {
      // streamType: 0 = high quality, 1 = low quality
      await client.setRemoteVideoStreamType(uid, streamType);
      console.log(`[AgoraMeetingProvider] Set stream type for ${uid} to ${streamType === 0 ? 'HIGH' : 'LOW'}`);
    } catch (err) {
      console.warn(`[AgoraMeetingProvider] Failed to set stream type for ${uid}:`, err);
    }
  }, [client]);

  // Stable callback for registering callbacks (extracted to prevent new reference each render)
  const registerCallbacks = useCallback((callbacks) => {
    callbacksRef.current = { ...callbacksRef.current, ...callbacks };
  }, []);

  // Route ALL remote audio playback (regular + screen-share) to the given output device.
  // Must be called whenever the user picks a new speaker — DOM setSinkId() does not
  // work on Agora's internally-played remote audio tracks.
  const setPlaybackDevice = useCallback(async (deviceId) => {
    if (!deviceId) return;
    playbackDeviceIdRef.current = deviceId;
    const apply = (audioTrack) => {
      if (audioTrack && typeof audioTrack.setPlaybackDevice === 'function') {
        return audioTrack.setPlaybackDevice(deviceId).catch(err => {
          console.warn('[AgoraContext] setPlaybackDevice failed:', err);
        });
      }
      return null;
    };
    const promises = [];
    remoteUsersRef.current?.forEach((u) => promises.push(apply(u?.audioTrack)));
    remoteScreenSharesRef.current?.forEach((s) => promises.push(apply(s?.audioTrack)));
    // Also try the live Agora client list as a safety net (state can lag SDK).
    (clientRef.current?.remoteUsers || []).forEach((u) => promises.push(apply(u?.audioTrack)));
    await Promise.all(promises);
  }, []);

  const value = useMemo(() => ({
    // Raw Agora objects
    client,
    screenClient,
    localAudioTrack,
    localVideoTrack,
    localScreenTrack,
    screenCaptureTrackRef, // Raw screen capture for PiP preview

    // Meeting state
    meetingState,
    meetingId,

    // VideoSDK-compatible interface
    ...mMeeting,

    // Simulcasting - set remote video stream type
    setRemoteVideoStreamType,

    // Network quality per participant
    networkQualities,

    // Register callbacks (for useMeeting hook compatibility)
    registerCallbacks,

    // Session-only display name update (updates the same variable used at join time)
    setParticipantName,
    setRemoteUserMetadata,

    // Route remote audio playback to a specific output device (speaker selection)
    setPlaybackDevice,
  }), [
    client, screenClient, localAudioTrack, localVideoTrack, localScreenTrack,
    meetingState, meetingId, mMeeting, setRemoteVideoStreamType, registerCallbacks, networkQualities,
    setPlaybackDevice,
  ]);

  return (
    <AgoraContext.Provider value={value}>
      {children}
    </AgoraContext.Provider>
  );
}

// useMeeting hook - VideoSDK-compatible interface
export function useAgoraMeeting(callbacks = {}) {
  const context = useContext(AgoraContext);
  if (!context) {
    throw new Error('useAgoraMeeting must be used within AgoraMeetingProvider');
  }

  // Register callbacks - must happen before join
  // Using layout effect to ensure it runs before auto-join effect
  useEffect(() => {
    console.log('[useAgoraMeeting] Registering callbacks:', Object.keys(callbacks || {}));
    if (callbacks && Object.keys(callbacks).length > 0) {
      context.registerCallbacks(callbacks);
    }
  }, []);

  return context;
}

// useParticipant hook - VideoSDK-compatible interface
// Helper: Build a VideoSDK-compatible stats object from Agora stats
// Local video stats: sendBitrate, sendFrameRate, sendResolutionWidth/Height, sendJitterMs, sendRttMs, sendPacketsLost, sendPackets, currentPacketLossRate, codecType
// Remote video stats: receiveBitrate, receiveFrameRate, receiveResolutionWidth/Height, receiveDelay, receivePacketsLost, receivePackets, packetLossRate, currentPacketLossRate, codecType
function buildVideoStats(agoraStats, globalRtt) {
  // Use per-track RTT if available (local stats have sendRttMs), otherwise global RTT
  const rtt = agoraStats.sendRttMs ?? globalRtt ?? 0;
  // Jitter: local has sendJitterMs, remote has receiveDelay (transport delay)
  const jitter = agoraStats.sendJitterMs ?? agoraStats.receiveDelay ?? 0;
  // Packet loss: compute from sent/received packets, or use currentPacketLossRate
  // currentPacketLossRate is a fraction (e.g., 0.05 = 5%)
  const lossRate = agoraStats.currentPacketLossRate ?? agoraStats.packetLossRate ?? 0;
  const totalPackets = agoraStats.sendPackets ?? agoraStats.receivePackets ?? 100;
  const packetsLost = agoraStats.sendPacketsLost ?? agoraStats.receivePacketsLost ?? Math.round(lossRate * totalPackets);

  return [{
    rtt,
    jitter,
    packetsLost,
    totalPackets: totalPackets || 100,
    bitrate: ((agoraStats.receiveBitrate ?? agoraStats.sendBitrate ?? 0) / 1000), // bps -> kb/s
    codec: agoraStats.codecType || '-',
    size: {
      framerate: agoraStats.receiveFrameRate ?? agoraStats.renderFrameRate ?? agoraStats.sendFrameRate ?? agoraStats.captureFrameRate ?? null,
      width: agoraStats.receiveResolutionWidth ?? agoraStats.sendResolutionWidth ?? null,
      height: agoraStats.receiveResolutionHeight ?? agoraStats.sendResolutionHeight ?? null,
    },
    currentSpatialLayer: null,
    currentTemporalLayer: null,
    preferredSpatialLayer: null,
    preferredTemporalLayer: null,
  }];
}

function buildAudioStats(agoraStats, globalRtt) {
  const rtt = agoraStats.sendRttMs ?? globalRtt ?? 0;
  const jitter = agoraStats.sendJitterMs ?? agoraStats.receiveDelay ?? 0;
  const totalPackets = agoraStats.sendPackets ?? agoraStats.receivePackets ?? 100;
  const lossRate = agoraStats.currentPacketLossRate ?? agoraStats.packetLossRate ?? 0;
  const packetsLost = agoraStats.sendPacketsLost ?? agoraStats.receivePacketsLost ?? Math.round(lossRate * totalPackets);

  return [{
    rtt,
    jitter,
    packetsLost,
    totalPackets: totalPackets || 100,
    bitrate: ((agoraStats.receiveBitrate ?? agoraStats.sendBitrate ?? 0) / 1000),
    codec: agoraStats.codecType || '-',
    size: { framerate: null, width: null, height: null },
  }];
}

export function useAgoraParticipant(participantId, callbacks = {}) {
  const context = useContext(AgoraContext);
  const participants = context?.participants;
  const setRemoteVideoStreamType = context?.setRemoteVideoStreamType;
  const agoraClient = context?.client;
  const agoraScreenClient = context?.screenClient;
  // Get pin/unpin functions from context to allow per-participant pinning
  const contextPin = context?.pin;
  const contextUnpin = context?.unpin;

  // IMPROVED PARTICIPANT RESOLUTION:
  // Priority order for finding participants:
  // 1. Direct lookup by exact key (preserving type)
  // 2. Lookup by numeric version of ID (for remote participants stored with numeric UIDs)
  // 3. Lookup by string version of ID
  // 4. Iterate to find remote participant by stringified key comparison
  // 5. ONLY as last resort: check if ID matches local participant's hashed numeric UID

  let participantData = null;

  // Step 1: Direct lookup by exact key
  if (participantId != null && participants) {
    participantData = participants.get(participantId);
  }

  // Step 2: Try numeric version of the ID (important for remote participants)
  // Remote participants are stored with numeric UIDs from Agora
  if (!participantData && participantId != null) {
    const numericId = Number(participantId);
    if (!isNaN(numericId)) {
      participantData = participants?.get(numericId);
    }
  }

  // Step 3: Try string version of the ID
  if (!participantData && participantId != null) {
    participantData = participants?.get(String(participantId));
  }

  // Step 4: Iterate through participants to find by stringified comparison
  // This handles edge cases where types don't match but values are equivalent
  // CRITICAL: Check ALL remote participants FIRST before any local fallback
  if (!participantData && participantId != null && participants) {
    const participantIdStr = String(participantId);

    // First pass: Look for remote participants only
    for (const [key, value] of participants) {
      if (value?.isLocal) continue; // Skip local participant in first pass

      // Check if the key matches as string
      if (String(key) === participantIdStr) {
        participantData = value;
        break;
      }
      // Check if the participant's ID matches
      if (value?.id != null && String(value.id) === participantIdStr) {
        participantData = value;
        break;
      }
    }
  }

  // Step 5: ONLY as absolute last resort, check if it matches local participant
  // This MUST come after checking all remote participants to prevent
  // hash collisions from incorrectly returning local data for remote UIDs
  if (!participantData && participantId != null && context?.localParticipant) {
    const localParticipant = context.localParticipant;
    const localNumericUid = stringToNumericUid(localParticipant?.id);
    const participantIdStr = String(participantId);

    // Check if this ID matches the local participant's string ID
    if (localParticipant?.id && String(localParticipant.id) === participantIdStr) {
      participantData = localParticipant;
    }
    // Or if it matches the local participant's hashed numeric UID
    else if (participantIdStr === String(localNumericUid)) {
      // EXTRA SAFETY: Double-check there's no remote participant we missed
      let hasRemoteMatch = false;
      if (participants) {
        for (const [key, value] of participants) {
          if (!value?.isLocal && (String(key) === participantIdStr || String(value?.id) === participantIdStr)) {
            hasRemoteMatch = true;
            participantData = value;
            break;
          }
        }
      }
      // Only use local participant if we're absolutely sure no remote matches
      if (!hasRemoteMatch) {
        participantData = localParticipant;
      }
    }
  }

  // Default return when participant not found
  const defaultReturn = {
    webcamStream: null,
    micStream: null,
    screenShareStream: null,
    webcamOn: false,
    micOn: false,
    screenShareOn: false,
    isLocal: false,
    displayName: '',
    isActiveSpeaker: false,
    pinState: { cam: false, share: false },
    mode: AgoraConstants.modes.SEND_AND_RECV,
    participant: null,
    metaData: {},
    setQuality: () => { },
    enableWebcam: () => { },
    disableWebcam: () => { },
    enableMic: () => { },
    disableMic: () => { },
    pin: () => { },
    unpin: () => { },
    getVideoStats: async () => ([]),
    getAudioStats: async () => ([]),
    getShareStats: async () => ([]),
    getShareAudioStats: async () => ([]),
    consumeWebcamStreams: () => { },
    stopConsumingWebcamStreams: () => { },
  };

  if (!participantData) {
    return { ...defaultReturn };
  }

  // Build VideoSDK-compatible participant object
  const participantObj = {
    id: participantData.id,
    displayName: participantData.displayName,
    metaData: participantData.metaData || {},
  };

  // Check if this participant is the active speaker.
  // Uses instantSpeakerId (raw ~200ms signal) so the green-border indicator
  // on sidebar/thumbnail tiles lights up immediately — NOT activeSpeakerId
  // which has a 600ms debounce and lags noticeably behind real speech.
  // Focus tile swaps are still driven by activeSpeakerId separately.
  const isActiveSpeaker = (() => {
    const speakerId = context?.instantSpeakerId ?? context?.activeSpeakerId;
    if (!speakerId) return false;
    const activeSpeakerStr = String(speakerId);
    const participantIdStr = String(participantId);
    const participantDataIdStr = String(participantData?.id);

    // Direct match with participantId or participantData.id
    if (activeSpeakerStr === participantIdStr) return true;
    if (activeSpeakerStr === participantDataIdStr) return true;

    // Check if the active speaker matches the participant's numeric UID (if stored)
    if (participantData?.numericUid && String(participantData.numericUid) === activeSpeakerStr) return true;

    // ONLY for LOCAL participant: check if the active speaker matches the hashed numeric UID
    // For remote participants, their ID IS already a numeric UID, so don't hash it again
    if (participantData?.isLocal && participantData?.id) {
      const numericUid = stringToNumericUid(participantData.id);
      if (String(numericUid) === activeSpeakerStr) return true;
    }

    return false;
  })();

  return {
    ...participantData,
    participant: participantObj,
    pinState: participantData.pinState ? { cam: true, share: false } : { cam: false, share: false },
    isActiveSpeaker,
    // Pin this participant (for spotlight/presenter feature)
    pin: () => {
      if (contextPin) {
        console.log(`[useAgoraParticipant] pin() called for participant ${participantId}`);
        contextPin(participantId);
      }
    },
    // Unpin this participant
    unpin: () => {
      if (contextUnpin) {
        console.log(`[useAgoraParticipant] unpin() called for participant ${participantId}`);
        contextUnpin(participantId);
      }
    },
    setQuality: (quality) => {
      // Use Agora's setRemoteVideoStreamType for simulcasting
      // Only applies to remote participants (not local)
      if (!participantData.isLocal && setRemoteVideoStreamType) {
        // Map quality levels to Agora stream types
        // 0 = high quality stream, 1 = low quality stream
        const streamType = quality === 'high' ? 0 : 1;
        setRemoteVideoStreamType(participantId, streamType);
        console.log(`[useAgoraParticipant] Quality set to ${quality} (streamType: ${streamType}) for participant ${participantId}`);
      }
    },
    getVideoStats: async () => {
      try {
        if (!agoraClient) return [];
        const rtt = (agoraClient.getRTCStats?.() || {}).RTT || 0;
        if (participantData.isLocal) {
          const stats = agoraClient.getLocalVideoStats?.();
          return stats ? buildVideoStats(stats, rtt) : [];
        } else {
          const allRemote = agoraClient.getRemoteVideoStats?.() || {};
          const uid = participantData.id;
          const stats = allRemote[uid] || allRemote[String(uid)] || allRemote[Number(uid)];
          return stats ? buildVideoStats(stats, rtt) : [];
        }
      } catch (e) {
        console.warn('[useAgoraParticipant] getVideoStats error:', e);
        return [];
      }
    },
    getAudioStats: async () => {
      try {
        if (!agoraClient) return [];
        const rtt = (agoraClient.getRTCStats?.() || {}).RTT || 0;
        if (participantData.isLocal) {
          const stats = agoraClient.getLocalAudioStats?.();
          return stats ? buildAudioStats(stats, rtt) : [];
        } else {
          const allRemote = agoraClient.getRemoteAudioStats?.() || {};
          const uid = participantData.id;
          const stats = allRemote[uid] || allRemote[String(uid)] || allRemote[Number(uid)];
          return stats ? buildAudioStats(stats, rtt) : [];
        }
      } catch (e) {
        console.warn('[useAgoraParticipant] getAudioStats error:', e);
        return [];
      }
    },
    getShareStats: async () => {
      try {
        // Screen share uses a separate client; for local, use screenClient; for remote, use main client with screen share UID
        if (participantData.isLocal) {
          if (!agoraScreenClient) return [];
          const rtt = (agoraScreenClient.getRTCStats?.() || {}).RTT || 0;
          const stats = agoraScreenClient.getLocalVideoStats?.();
          return stats ? buildVideoStats(stats, rtt) : [];
        } else {
          if (!agoraClient) return [];
          const rtt = (agoraClient.getRTCStats?.() || {}).RTT || 0;
          const screenShareUid = Number(participantData.id) + 1;
          const allRemote = agoraClient.getRemoteVideoStats?.() || {};
          const stats = allRemote[screenShareUid] || allRemote[String(screenShareUid)];
          return stats ? buildVideoStats(stats, rtt) : [];
        }
      } catch (e) {
        console.warn('[useAgoraParticipant] getShareStats error:', e);
        return [];
      }
    },
    getShareAudioStats: async () => {
      try {
        if (participantData.isLocal) {
          if (!agoraScreenClient) return [];
          const rtt = (agoraScreenClient.getRTCStats?.() || {}).RTT || 0;
          const stats = agoraScreenClient.getLocalAudioStats?.();
          return stats ? buildAudioStats(stats, rtt) : [];
        } else {
          if (!agoraClient) return [];
          const rtt = (agoraClient.getRTCStats?.() || {}).RTT || 0;
          const screenShareUid = Number(participantData.id) + 1;
          const allRemote = agoraClient.getRemoteAudioStats?.() || {};
          const stats = allRemote[screenShareUid] || allRemote[String(screenShareUid)];
          return stats ? buildAudioStats(stats, rtt) : [];
        }
      } catch (e) {
        console.warn('[useAgoraParticipant] getShareAudioStats error:', e);
        return [];
      }
    },
    consumeWebcamStreams: () => {
      // Agora handles this automatically
    },
    stopConsumingWebcamStreams: () => {
      // Agora handles this automatically
    },
  };
}

// useMediaDevice hook - VideoSDK-compatible interface
export function useAgoraMediaDevice(options = {}) {
  const { onDeviceChanged } = options;

  useEffect(() => {
    if (onDeviceChanged) {
      AgoraRTC.onMicrophoneChanged = onDeviceChanged;
      AgoraRTC.onCameraChanged = onDeviceChanged;
      AgoraRTC.onPlaybackDeviceChanged = onDeviceChanged;
    }

    return () => {
      AgoraRTC.onMicrophoneChanged = null;
      AgoraRTC.onCameraChanged = null;
      AgoraRTC.onPlaybackDeviceChanged = null;
    };
  }, [onDeviceChanged]);

  return {
    getMicrophones: AgoraRTC.getMicrophones,
    getCameras: AgoraRTC.getCameras,
    getPlaybackDevices: AgoraRTC.getPlaybackDevices,
    requestPermission: async (type) => {
      // Returns a Map to match VideoSDK interface
      // Uses navigator.mediaDevices.getUserMedia for reliability — Agora's
      // createCameraVideoTrack can fail with DEVICE_NOT_FOUND even when
      // the camera is available.
      const resultMap = new Map();
      try {
        if (type === 'audio') {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream.getTracks().forEach(t => t.stop());
          resultMap.set('audio', true);
        } else if (type === 'video') {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true });
          stream.getTracks().forEach(t => t.stop());
          resultMap.set('video', true);
        }
      } catch (error) {
        console.error(`Failed to get ${type} permission:`, error);
        if (type === 'audio') {
          resultMap.set('audio', false);
        } else if (type === 'video') {
          resultMap.set('video', false);
        }
      }
      return resultMap;
    },
    checkPermissions: async () => {
      // Returns a Map to match VideoSDK interface
      const resultMap = new Map();
      resultMap.set('audio', false);
      resultMap.set('video', false);

      // Use navigator.mediaDevices.getUserMedia for permission checks instead of
      // AgoraRTC.createCameraVideoTrack — Agora's track creation can fail with
      // DEVICE_NOT_FOUND on some systems even when the camera is available,
      // especially if enumerateDevices hasn't been primed yet.
      try {
        const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        audioStream.getTracks().forEach(t => t.stop());
        resultMap.set('audio', true);
      } catch (error) {
        console.log('Audio permission not available:', error.message);
      }

      try {
        const videoStream = await navigator.mediaDevices.getUserMedia({ video: true });
        videoStream.getTracks().forEach(t => t.stop());
        resultMap.set('video', true);
      } catch (error) {
        console.log('Video permission not available:', error.message);
      }

      return resultMap;
    },
  };
}

// Export Constants for compatibility
export const Constants = AgoraConstants;

// Export stringToNumericUid for use in other files (e.g., matching RTM IDs to RTC UIDs)
export { stringToNumericUid };

export default AgoraContext;
